import { useState, useEffect, useCallback } from "react";
import { API, CURRENCIES } from "@/lib/constants";
import { fetchMyWorkspaces } from "@/lib/workspaceFetch";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Mail, Clock, ShieldCheck, BookOpen, AlertTriangle, Activity,
  Users, Save, Plus, Trash2, ToggleLeft, ToggleRight, RefreshCw,
  ChevronRight, FileText, ExternalLink, Eye, EyeOff, Key,
  Signature, Bell, LogOut, User, Shield, Settings2, Pencil, Check, X,
  Upload, Download, Globe, FileArchive, Loader2, FolderOpen, Edit2, Link2,
  Target, Send, BarChart3, TrendingUp, TrendingDown, Play, Award, Zap, Trophy,
  DollarSign, Calendar, Palette,
} from "lucide-react";
import { SystemStatusPanel } from "../components/SystemStatusPanel";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import OneDriveSettings from "@/components/OneDriveSettings";
import PlatformHealthWidget from "@/components/PlatformHealthWidget";
import CoverLibraryAdmin from "@/components/CoverLibraryAdmin";

const TABS = [
  { id: "rex-ops", label: "Rex Operations", icon: Activity },
  { id: "system-status", label: "System Status", icon: ShieldCheck },
  { id: "ai-models", label: "AI Models", icon: Settings2 },
  { id: "memory", label: "Rex Memory", icon: BookOpen },
  { id: "email", label: "Email & Briefings", icon: Mail },
  { id: "senders", label: "Email Senders", icon: Send },
  { id: "targets", label: "Revenue Targets", icon: Target },
  { id: "pipeline", label: "Pipeline Settings", icon: TrendingUp },
  { id: "fx", label: "FX Rates", icon: Globe },
  { id: "data", label: "Data Management", icon: FileArchive },
  { id: "approvers", label: "Content Approvers", icon: ShieldCheck },
  { id: "jobs", label: "Scheduled Jobs", icon: Clock },
  { id: "waits", label: "Waits & Triage", icon: Clock },
  { id: "authority", label: "Authority Controls", icon: ShieldCheck },
  { id: "knowledge", label: "Knowledge Base", icon: BookOpen },
  { id: "risk", label: "Risk & Overrides", icon: AlertTriangle },
  { id: "integrations", label: "Integrations", icon: Settings2 },
  { id: "cover-library", label: "Cover library", icon: Palette },
  { id: "health", label: "System Health", icon: Activity },
];

// ── Tab: Waits & Triage config (Sep 2026) — intervals + loss vocabulary, no deploy ──
function WaitsSettingsTab() {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => fetch(`${API}/waits/config`, { credentials: "include", cache: "no-store" })
    .then(r => r.ok ? r.json() : null).then(d => d && setCfg(d));
  useEffect(() => { load(); }, []);
  if (!cfg) return <div className="text-sm text-slate-400 py-8 text-center">Loading…</div>;

  const setNum = (k, v) => setCfg({ ...cfg, [k]: parseInt(v || "0", 10) });
  const vocab = cfg.loss_vocabulary || { what_happened: [], account: [] };
  const save = async () => {
    setSaving(true);
    const payload = {
      first_chase_interval_days: cfg.first_chase_interval_days,
      second_chase_interval_days: cfg.second_chase_interval_days,
      max_chases: cfg.max_chases,
      aged_task_candidate_days: cfg.aged_task_candidate_days,
      loss_vocabulary: {
        what_happened: (vocab.what_happened || []).map(s => s.trim()).filter(Boolean),
        account: (vocab.account || []).map(s => s.trim()).filter(Boolean),
      },
    };
    const r = await fetch(`${API}/waits/config`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      credentials: "include", body: JSON.stringify(payload),
    });
    if (r.ok) { toast.success("Waits config saved"); load(); } else { toast.error("Save failed (admin only)"); }
    setSaving(false);
  };

  return (
    <div className="space-y-4" data-testid="waits-settings-tab">
      <Card>
        <CardHeader><CardTitle className="text-base">Chase ladder</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-slate-600">First chase after (days) — default offered when a wait is set</label>
            <Input type="number" value={cfg.first_chase_interval_days} onChange={e => setNum("first_chase_interval_days", e.target.value)} data-testid="cfg-first-chase" />
          </div>
          <div>
            <label className="text-xs text-slate-600">Second chase gap (days)</label>
            <Input type="number" value={cfg.second_chase_interval_days} onChange={e => setNum("second_chase_interval_days", e.target.value)} data-testid="cfg-second-chase" />
          </div>
          <div>
            <label className="text-xs text-slate-600">Max chases before a decision is forced</label>
            <Input type="number" value={cfg.max_chases} onChange={e => setNum("max_chases", e.target.value)} data-testid="cfg-max-chases" />
          </div>
          <div>
            <label className="text-xs text-slate-600">Aged-candidate threshold (days) — board tasks older than this surface to triage</label>
            <Input type="number" value={cfg.aged_task_candidate_days} onChange={e => setNum("aged_task_candidate_days", e.target.value)} data-testid="cfg-aged-days" />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle className="text-base">Loss vocabulary</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div>
            <label className="text-xs text-slate-600">What happened — one category per line</label>
            <textarea rows={11} value={(vocab.what_happened || []).join("\n")}
              onChange={e => setCfg({ ...cfg, loss_vocabulary: { ...vocab, what_happened: e.target.value.split("\n") } })}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono outline-none focus:border-slate-500"
              data-testid="cfg-what-happened" />
          </div>
          <div>
            <label className="text-xs text-slate-600">Whose account — one per line (client_stated / our_assessment)</label>
            <textarea rows={2} value={(vocab.account || []).join("\n")}
              onChange={e => setCfg({ ...cfg, loss_vocabulary: { ...vocab, account: e.target.value.split("\n") } })}
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono outline-none focus:border-slate-500"
              data-testid="cfg-account" />
          </div>
        </CardContent>
      </Card>

      <Button onClick={save} disabled={saving} data-testid="cfg-save" className="bg-slate-900 hover:bg-slate-800 text-white">
        {saving ? "Saving…" : "Save changes"}
      </Button>
    </div>
  );
}


// ── Tab: Email Senders (who can send campaign emails) ──
function EmailSendersTab() {
  const [senders, setSenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [draft, setDraft] = useState({ display_name: "", email: "", title: "", signature_html: "" });
  const [editId, setEditId] = useState(null);
  const [editDraft, setEditDraft] = useState({ display_name: "", title: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/mission-control/senders`, { credentials: "include" });
      const d = await r.json();
      setSenders(d.senders || []);
    } catch {
      toast.error("Failed to load senders");
    }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const addSender = async () => {
    const email = (draft.email || "").trim().toLowerCase();
    if (!email.includes("@") || !draft.display_name.trim()) return toast.error("Name and a valid email are required");
    setBusy(true);
    try {
      const r = await fetch(`${API}/mission-control/senders`, {
        method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...draft, email }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || "Failed to add sender");
      toast.success(`${draft.display_name} can now be selected as a campaign sender`);
      setDraft({ display_name: "", email: "", title: "", signature_html: "" });
      setShowAdd(false);
      load();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const saveEdit = async (sid) => {
    setBusy(true);
    try {
      const r = await fetch(`${API}/mission-control/senders/${sid}`, {
        method: "PATCH", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editDraft),
      });
      if (!r.ok) throw new Error((await r.json())?.detail || "Failed");
      toast.success("Sender updated");
      setEditId(null);
      load();
    } catch (e) { toast.error(e.message); }
    setBusy(false);
  };

  const setActive = async (sid, isActive, name) => {
    if (!isActive && !window.confirm(`Deactivate ${name}? They'll no longer be selectable as a campaign sender (existing campaigns are unaffected).`)) return;
    setBusy(true);
    try {
      if (isActive) {
        await fetch(`${API}/mission-control/senders/${sid}`, {
          method: "PATCH", credentials: "include", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ is_active: true }),
        });
      } else {
        await fetch(`${API}/mission-control/senders/${sid}`, { method: "DELETE", credentials: "include" });
      }
      toast.success(isActive ? "Sender reactivated" : "Sender deactivated");
      load();
    } catch { toast.error("Action failed"); }
    setBusy(false);
  };

  const active = senders.filter((s) => s.is_active);
  const inactive = senders.filter((s) => !s.is_active);

  return (
    <div className="space-y-4" data-testid="email-senders-tab">
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-slate-500 max-w-2xl">
          People set up here can be chosen as the "from" identity when sending bespoke campaign emails.
          Use a real, monitored mailbox on an authenticated domain (e.g. <span className="font-mono">@resiligent.com</span>) —
          a 6-digit authorisation code is emailed to this address at send time.
        </p>
        <Button onClick={() => setShowAdd((v) => !v)} className="bg-indigo-600 hover:bg-indigo-700 text-white shrink-0" data-testid="sender-add-toggle">
          <Plus className="h-4 w-4 mr-1" /> Add sender
        </Button>
      </div>

      {showAdd && (
        <Card data-testid="sender-add-form">
          <CardContent className="p-4 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div>
                <Label className="text-xs">Name</Label>
                <Input value={draft.display_name} onChange={(e) => setDraft({ ...draft, display_name: e.target.value })} placeholder="Remi Dairo" data-testid="sender-name-input" />
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input value={draft.email} onChange={(e) => setDraft({ ...draft, email: e.target.value })} placeholder="you@resiligent.com" data-testid="sender-email-input" />
              </div>
              <div>
                <Label className="text-xs">Title (optional)</Label>
                <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })} placeholder="Managing Partner" data-testid="sender-title-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Email signature (optional)</Label>
              <Textarea value={draft.signature_html} onChange={(e) => setDraft({ ...draft, signature_html: e.target.value })} placeholder="Paste a signature (HTML kept)…" className="h-20" data-testid="sender-signature-input" />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowAdd(false)} data-testid="sender-add-cancel">Cancel</Button>
              <Button onClick={addSender} disabled={busy} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="sender-save-btn">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add sender"}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold uppercase tracking-wider text-slate-700">Active senders</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {loading ? <div className="h-10 bg-slate-100 animate-pulse rounded" /> : active.length === 0 ? (
            <p className="text-sm text-slate-400 italic">No active senders. Add one so campaigns can be sent.</p>
          ) : active.map((s) => (
            <div key={s.sender_id} className="flex items-center justify-between gap-2 bg-slate-50 border border-slate-200 rounded px-3 py-2" data-testid={`sender-row-${s.sender_id}`}>
              {editId === s.sender_id ? (
                <div className="flex-1 grid grid-cols-2 gap-2">
                  <Input value={editDraft.display_name} onChange={(e) => setEditDraft({ ...editDraft, display_name: e.target.value })} data-testid="sender-edit-name" />
                  <Input value={editDraft.title} onChange={(e) => setEditDraft({ ...editDraft, title: e.target.value })} placeholder="Title" data-testid="sender-edit-title" />
                </div>
              ) : (
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-800 truncate">{s.display_name}</p>
                  <p className="text-xs text-slate-500">{s.email}{s.title ? ` · ${s.title}` : ""}</p>
                </div>
              )}
              <div className="flex gap-1 shrink-0">
                {editId === s.sender_id ? (
                  <>
                    <Button size="sm" onClick={() => saveEdit(s.sender_id)} disabled={busy} data-testid={`sender-edit-save-${s.sender_id}`}>Save</Button>
                    <Button size="sm" variant="ghost" onClick={() => setEditId(null)}>Cancel</Button>
                  </>
                ) : (
                  <>
                    <Button size="sm" variant="ghost" onClick={() => { setEditId(s.sender_id); setEditDraft({ display_name: s.display_name, title: s.title || "" }); }} data-testid={`sender-edit-${s.sender_id}`}>Edit</Button>
                    <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => setActive(s.sender_id, false, s.display_name)} disabled={busy} data-testid={`sender-deactivate-${s.sender_id}`}>Deactivate</Button>
                  </>
                )}
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {inactive.length > 0 && (
        <Card>
          <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold uppercase tracking-wider text-slate-400">Deactivated</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {inactive.map((s) => (
              <div key={s.sender_id} className="flex items-center justify-between gap-2 bg-slate-50 border border-slate-100 rounded px-3 py-2 opacity-70" data-testid={`sender-row-${s.sender_id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-600 truncate">{s.display_name}</p>
                  <p className="text-xs text-slate-400">{s.email}</p>
                </div>
                <Button size="sm" variant="ghost" className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 shrink-0" onClick={() => setActive(s.sender_id, true, s.display_name)} disabled={busy} data-testid={`sender-reactivate-${s.sender_id}`}>Reactivate</Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── Tab: Content Approvers (Feb 2026) ──
function ContentApproversTab() {
  const [emails, setEmails] = useState([]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/content-studio/approvers`, { credentials: "include" });
      const d = await r.json();
      setEmails(d.approver_emails || []);
    } catch {
      toast.error("Failed to load approvers");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async (next) => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/content-studio/approvers`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ approver_emails: next }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || "Failed");
      setEmails(d.approver_emails || []);
      toast.success("Approvers updated");
    } catch (e) {
      toast.error(e.message);
    }
    setSaving(false);
  };

  const addOne = () => {
    const e = (draft || "").trim().toLowerCase();
    if (!e || !e.includes("@")) return toast.error("Enter a valid email");
    if (emails.includes(e)) return toast.error("Already an approver");
    save([...emails, e]);
    setDraft("");
  };

  const removeOne = (email) => {
    if (!window.confirm(`Remove ${email} as approver? They'll lose approver permissions on both legacy Content Studio and v3 Amplify.`)) return;
    save(emails.filter((x) => x !== email));
  };

  return (
    <div className="space-y-4" data-testid="content-approvers-tab">
      <p className="text-sm text-slate-500">
        Approvers can sign-off on Remi (personal) and RESILIGENT (institutional) posts before they're scheduled.
        Updates here sync to both the legacy Content Studio and the v3 Amplify Planner.
        Employee posts are self-serve and do not require approval.
      </p>
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-sm font-semibold uppercase tracking-wider text-slate-700">Configured approvers</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {loading ? <div className="h-10 bg-slate-100 animate-pulse rounded" /> : emails.length === 0 ? (
            <p className="text-sm text-slate-400 italic">No approvers configured. Posts can't be approved until you add at least one.</p>
          ) : (
            <ul className="space-y-2">
              {emails.map((e) => (
                <li key={e} className="flex justify-between items-center bg-slate-50 border border-slate-200 rounded px-3 py-2 text-sm" data-testid={`approver-row-${e}`}>
                  <span className="font-mono text-slate-700">{e}</span>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => removeOne(e)}
                    disabled={saving}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    data-testid={`approver-remove-${e}`}
                  >Remove</Button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex gap-2 pt-2 border-t border-slate-200">
            <input
              type="email"
              placeholder="someone@resiligent.com"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addOne()}
              className="flex-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-900"
              data-testid="approver-add-input"
            />
            <Button onClick={addOne} disabled={saving} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="approver-add-btn">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add approver"}
            </Button>
          </div>
        </CardContent>
      </Card>
      <TrustedPostersPanel />
    </div>
  );
}


// ── Sub-panel: Trusted Posters ──
// Employees on this list have their Employee Posts auto-approved on creation
// (skipping the IN_REVIEW → PENDING_APPROVAL gates). Use sparingly.
function TrustedPostersPanel() {
  const [emails, setEmails] = useState([]);
  const [draft, setDraft] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/content-studio/trusted-posters`, { credentials: "include" });
      const d = await r.json();
      setEmails(d.emails || []);
    } catch {
      toast.error("Failed to load trusted posters");
    }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const save = async (next) => {
    setSaving(true);
    try {
      const r = await fetch(`${API}/content-studio/trusted-posters`, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ emails: next }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d?.detail || "Failed");
      setEmails(d.emails || []);
      toast.success("Trusted posters updated");
    } catch (e) {
      toast.error(e.message);
    }
    setSaving(false);
  };

  const addOne = () => {
    const e = (draft || "").trim().toLowerCase();
    if (!e || !e.includes("@")) return toast.error("Enter a valid email");
    if (emails.includes(e)) return toast.error("Already trusted");
    save([...emails, e]);
    setDraft("");
  };

  const removeOne = (email) => {
    if (!window.confirm(`Remove ${email} from trusted posters? Their future Employee Posts will go through normal approval.`)) return;
    save(emails.filter((x) => x !== email));
  };

  return (
    <Card data-testid="trusted-posters-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold uppercase tracking-wider text-slate-700">Trusted Posters</CardTitle>
        <p className="text-xs text-slate-500 mt-1">Employees on this list have their Employee Posts auto-approved at creation time, skipping the In-Review → Pending-Approval gates. Their drafts go straight to Approved and can be downloaded immediately.</p>
      </CardHeader>
      <CardContent className="space-y-3">
        {loading ? <div className="h-10 bg-slate-100 animate-pulse rounded" /> : emails.length === 0 ? (
          <p className="text-sm text-slate-400 italic">No trusted posters configured — all Employee Posts go through normal approval.</p>
        ) : (
          <ul className="space-y-2">
            {emails.map((e) => (
              <li key={e} className="flex justify-between items-center bg-emerald-50 border border-emerald-200 rounded px-3 py-2 text-sm" data-testid={`trusted-poster-row-${e}`}>
                <span className="font-mono text-slate-700">{e}</span>
                <Badge variant="outline" className="text-[10px] bg-emerald-100 text-emerald-700 border-emerald-300 mr-auto ml-3">Auto-approved</Badge>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => removeOne(e)}
                  disabled={saving}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid={`trusted-poster-remove-${e}`}
                >Remove</Button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex gap-2 pt-2 border-t border-slate-200">
          <input
            type="email"
            placeholder="employee@resiligent.com"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addOne()}
            className="flex-1 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-900"
            data-testid="trusted-poster-add-input"
          />
          <Button onClick={addOne} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="trusted-poster-add-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add trusted poster"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Tab: Email & Briefing Recipients ──
function EmailRecipientsTab() {
  const [recipients, setRecipients] = useState({ managing_partner: [], sales: [], strategy: [] });
  const [newEmails, setNewEmails] = useState({ managing_partner: "", sales: "", strategy: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch(`${API}/admin-console/email-recipients`, { credentials: "include" })
      .then((r) => r.json()).then(setRecipients).catch(() => {});
  }, []);

  const addEmail = (role) => {
    const email = newEmails[role].trim().toLowerCase();
    if (!email || !email.includes("@")) return toast.error("Enter a valid email");
    if (recipients[role].includes(email)) return toast.error("Already added");
    setRecipients((p) => ({ ...p, [role]: [...p[role], email] }));
    setNewEmails((p) => ({ ...p, [role]: "" }));
  };

  const removeEmail = (role, email) => {
    setRecipients((p) => ({ ...p, [role]: p[role].filter((e) => e !== email) }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin-console/email-recipients`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(recipients),
      });
      if (res.ok) toast.success("Email recipients updated");
      else toast.error("Failed to save");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const roleLabels = { managing_partner: "Managing Partner", sales: "Sales Team", strategy: "Strategy / Country" };

  return (
    <div className="space-y-5" data-testid="email-recipients-tab">
      <p className="text-sm text-slate-500">Configure who receives the Morning Orchestrator intelligence briefs by role.</p>
      {Object.entries(roleLabels).map(([role, label]) => (
        <Card key={role}>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold uppercase tracking-wider text-slate-500">{label}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex flex-wrap gap-2">
              {recipients[role].map((email) => (
                <Badge key={email} variant="secondary" className="gap-1 pr-1">
                  {email}
                  <button onClick={() => removeEmail(role, email)} className="ml-1 hover:text-red-600"><X className="h-3 w-3" /></button>
                </Badge>
              ))}
              {recipients[role].length === 0 && <span className="text-xs text-slate-400">No recipients configured</span>}
            </div>
            <div className="flex gap-2 mt-2">
              <Input
                data-testid={`add-email-${role}`}
                placeholder="email@example.com"
                value={newEmails[role]}
                onChange={(e) => setNewEmails((p) => ({ ...p, [role]: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && addEmail(role)}
                className="h-8 text-sm"
              />
              <Button size="sm" variant="outline" onClick={() => addEmail(role)} className="h-8 px-3">
                <Plus className="h-3.5 w-3.5" />
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
      <Button onClick={handleSave} disabled={saving} data-testid="save-recipients-btn">
        <Save className="h-4 w-4 mr-2" />{saving ? "Saving..." : "Save Recipients"}
      </Button>
    </div>
  );
}

// ── Tab: Scheduled Jobs ──
function ScheduledJobsTab() {
  const [jobs, setJobs] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [toggling, setToggling] = useState(null);
  const [triggering, setTriggering] = useState(null);
  const [dashLoading, setDashLoading] = useState(true);
  const [activeSection, setActiveSection] = useState("runs");

  const fetchJobs = useCallback(() => {
    fetch(`${API}/admin-console/jobs`, { credentials: "include" })
      .then((r) => r.json()).then((d) => setJobs(d.jobs || [])).catch(() => {});
  }, []);

  const fetchDashboard = useCallback(() => {
    setDashLoading(true);
    fetch(`${API}/admin-console/jobs-dashboard`, { credentials: "include" })
      .then((r) => r.json()).then(setDashboard).catch(() => {})
      .finally(() => setDashLoading(false));
  }, []);

  useEffect(() => { fetchJobs(); fetchDashboard(); }, [fetchJobs, fetchDashboard]);

  const toggleJob = async (jobId) => {
    setToggling(jobId);
    try {
      const res = await fetch(`${API}/admin-console/jobs/${jobId}/toggle`, { method: "PUT", credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setJobs((prev) => prev.map((j) => j.job_id === jobId ? { ...j, enabled: data.enabled } : j));
        toast.success(`Job ${data.enabled ? "enabled" : "disabled"}`);
      }
    } catch { toast.error("Failed to toggle job"); }
    finally { setToggling(null); }
  };

  const triggerJob = async (jobName) => {
    setTriggering(jobName);
    try {
      const res = await fetch(`${API}/test/trigger-job`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ job_name: jobName, force: true }),
      });
      const data = await res.json();
      if (data.success) {
        toast.success(`${jobName} completed`);
        fetchDashboard();
      } else toast.error(data.error || "Job failed");
    } catch { toast.error("Failed to trigger job"); }
    finally { setTriggering(null); }
  };

  const [sendingBriefing, setSendingBriefing] = useState(false);
  const [briefingDiag, setBriefingDiag] = useState(null);
  const [showDiag, setShowDiag] = useState(false);

  const fetchBriefingDiagnostic = async () => {
    try {
      const res = await fetch(`${API}/admin/briefing-diagnostic`, { credentials: "include" });
      const data = await res.json();
      setBriefingDiag(data);
      setShowDiag(true);
    } catch { toast.error("Failed to fetch diagnostic"); }
  };

  const sendBriefingNow = async () => {
    setSendingBriefing(true);
    setBriefingDiag(null);
    setShowDiag(false);
    try {
      // Step 1: Generate the briefing (force=true bypasses idempotency AND clears today's outbox)
      toast.info("Generating morning briefing...");
      const briefRes = await fetch(`${API}/test/trigger-job`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ job_name: "morning_briefing", force: true }),
      });
      const briefData = await briefRes.json();
      if (!briefData.success) {
        toast.error(briefData.error || "Briefing generation failed");
        if (briefData.traceback) console.error("Traceback:", briefData.traceback);
        return;
      }
      const queued = briefData.result?.emails_queued || 0;
      const skipped = briefData.result?.emails_skipped || 0;
      if (queued === 0) {
        toast.warning(`No emails queued (${skipped} skipped) — check Email & Briefings recipients`);
        return;
      }
      toast.info(`${queued} emails queued, sending now...`);

      // Step 2: Process the outbox (actually send via SendGrid)
      const outRes = await fetch(`${API}/test/trigger-job`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ job_name: "process_outbox" }),
      });
      const outData = await outRes.json();
      if (outData.success) {
        const sent = outData.result?.sent || 0;
        const failed = outData.result?.failed || 0;
        if (failed > 0) toast.warning(`Sent ${sent}, failed ${failed} — check logs`);
        else toast.success(`Morning briefing sent to ${sent} recipients`);
      } else {
        toast.error("Briefing queued but outbox processing failed — check logs");
      }
      fetchDashboard();
    } catch (err) { toast.error("Failed to send briefing: " + err.message); }
    finally { setSendingBriefing(false); }
  };

  const severityColor = { HIGH: "bg-red-100 text-red-700 border-red-200", MED: "bg-amber-100 text-amber-700 border-amber-200", LOW: "bg-blue-100 text-blue-700 border-blue-200" };
  const statusColor = { completed: "text-emerald-600", running: "text-blue-600", failed: "text-red-600" };
  const outboxStatusColor = { sent: "bg-emerald-100 text-emerald-700", queued: "bg-amber-100 text-amber-700", failed: "bg-red-100 text-red-700" };

  const availableJobs = ["dormant_check", "proposal_followups", "cooling_deals", "critical_alerts", "opportunity_scoring", "morning_briefing", "process_outbox", "news_scan"];

  return (
    <div className="space-y-4" data-testid="scheduled-jobs-tab">
      {/* Send Briefing Now — prominent CTA */}
      <Card className="border-indigo-200 bg-indigo-50/50">
        <CardContent className="py-4 space-y-3">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-indigo-900">Morning Briefing</p>
              <p className="text-xs text-indigo-600 mt-0.5">Generate and send the intelligence briefing to all configured recipients now</p>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={fetchBriefingDiagnostic}
                variant="outline"
                size="sm"
                className="text-indigo-700 border-indigo-300 gap-1"
                data-testid="briefing-diagnostic-btn"
              >
                <Activity className="h-3.5 w-3.5" />
                Diagnostic
              </Button>
              <Button
                onClick={sendBriefingNow}
                disabled={sendingBriefing}
                className="bg-indigo-600 hover:bg-indigo-700 gap-2 whitespace-nowrap"
                size="sm"
                data-testid="send-briefing-now-btn"
              >
                {sendingBriefing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {sendingBriefing ? "Sending..." : "Send Briefing Now"}
              </Button>
            </div>
          </div>

          {/* Diagnostic Panel */}
          {showDiag && briefingDiag && (
            <div className="bg-white border border-indigo-200 rounded-lg p-3 text-xs space-y-2" data-testid="briefing-diagnostic-panel">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-indigo-900">Briefing Diagnostic</span>
                <button onClick={() => setShowDiag(false)} className="text-gray-400 hover:text-gray-600"><X className="h-3.5 w-3.5" /></button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <div className="bg-blue-50 p-2 rounded"><span className="text-gray-500">Signals (today)</span><br/><span className="font-bold">{briefingDiag.data_counts?.today_scan_signals || 0}</span></div>
                <div className="bg-blue-50 p-2 rounded"><span className="text-gray-500">Signals (3d)</span><br/><span className="font-bold">{briefingDiag.data_counts?.recent_3d_scan_signals || 0}</span></div>
                <div className="bg-blue-50 p-2 rounded"><span className="text-gray-500">Active Flags</span><br/><span className="font-bold">{briefingDiag.data_counts?.active_flags || 0}</span></div>
                <div className="bg-blue-50 p-2 rounded"><span className="text-gray-500">Active Opps</span><br/><span className="font-bold">{briefingDiag.data_counts?.active_opportunities || 0}</span></div>
              </div>
              <div>
                <span className="text-gray-500">Recipients:</span>
                {briefingDiag.recipients_config ? (
                  <span className="ml-1">{[...(briefingDiag.recipients_config.managing_partner||[]), ...(briefingDiag.recipients_config.sales||[]), ...(briefingDiag.recipients_config.strategy||[])].join(", ") || "NONE CONFIGURED"}</span>
                ) : <span className="ml-1 text-red-600 font-semibold">NOT CONFIGURED — go to Email & Briefings tab</span>}
              </div>
              {briefingDiag.today_outbox_entries?.length > 0 && (
                <div>
                  <span className="text-gray-500">Today's Outbox ({briefingDiag.today_outbox_entries.length}):</span>
                  <div className="mt-1 space-y-1">
                    {briefingDiag.today_outbox_entries.map((e, i) => (
                      <div key={i} className="flex items-center gap-2">
                        <Badge variant="outline" className={e.status === "sent" ? "bg-emerald-50 text-emerald-700" : e.status === "failed" ? "bg-red-50 text-red-700" : "bg-amber-50 text-amber-700"}>{e.status}</Badge>
                        <span>{e.recipient}</span>
                        <span className="text-gray-400">{e.role}</span>
                        {e.error && <span className="text-red-500 truncate max-w-[200px]">{e.error}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="flex gap-3 text-gray-500">
                <span>SendGrid: {briefingDiag.env_check?.REX_DIGEST_SENDGRID_KEY ? "OK" : <span className="text-red-600 font-semibold">MISSING</span>}</span>
                <span>From: {briefingDiag.env_check?.REX_DIGEST_FROM_EMAIL || "NOT SET"}</span>
              </div>
              {briefingDiag.last_signal && (
                <div className="text-gray-500">
                  Last signal: <span className="text-gray-700">{briefingDiag.last_signal.headline?.slice(0, 60)}...</span>
                  <span className="ml-1">({briefingDiag.last_signal.analyzed_at?.slice(0, 10)})</span>
                </div>
              )}
              <div className="text-gray-500">Total signals in DB: <span className="font-bold text-gray-700">{briefingDiag.data_counts?.total_scan_signals || 0}</span></div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary Cards */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="cursor-pointer hover:border-slate-400 transition-colors" onClick={() => setActiveSection("runs")} data-testid="jobs-card-runs">
            <CardContent className="pt-4 pb-3 text-center">
              <p className="text-2xl font-bold text-slate-800">{dashboard.job_runs?.count || 0}</p>
              <p className="text-xs text-slate-500 mt-0.5">Recent Job Runs</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:border-red-300 transition-colors" onClick={() => setActiveSection("flags")} data-testid="jobs-card-flags">
            <CardContent className="pt-4 pb-3 text-center">
              <p className="text-2xl font-bold text-red-600">{dashboard.flags?.count || 0}</p>
              <p className="text-xs text-slate-500 mt-0.5">Active Flags</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:border-emerald-300 transition-colors" onClick={() => setActiveSection("outbox")} data-testid="jobs-card-outbox">
            <CardContent className="pt-4 pb-3 text-center">
              <p className="text-2xl font-bold text-emerald-600">{dashboard.outbox?.sent || 0}</p>
              <p className="text-xs text-slate-500 mt-0.5">Emails Sent</p>
            </CardContent>
          </Card>
          <Card className="cursor-pointer hover:border-amber-300 transition-colors" onClick={() => setActiveSection("outbox")} data-testid="jobs-card-queued">
            <CardContent className="pt-4 pb-3 text-center">
              <p className="text-2xl font-bold text-amber-600">{dashboard.outbox?.queued || 0}</p>
              <p className="text-xs text-slate-500 mt-0.5">Queued Emails</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Manual Trigger Section */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold flex items-center gap-2"><RefreshCw className="h-4 w-4" />Manual Trigger</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {availableJobs.map((j) => (
              <Button key={j} variant="outline" size="sm" onClick={() => triggerJob(j)} disabled={triggering === j}
                className="text-xs" data-testid={`trigger-${j}`}>
                {triggering === j ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                {j.replace(/_/g, " ")}
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Section tabs */}
      <div className="flex gap-1 border-b pb-1">
        {[
          { id: "runs", label: "Job Runs" },
          { id: "flags", label: "Flags" },
          { id: "outbox", label: "Email Outbox" },
          { id: "config", label: "Job Config" },
        ].map((t) => (
          <button key={t.id} onClick={() => setActiveSection(t.id)} data-testid={`jobs-section-${t.id}`}
            className={`px-3 py-1.5 text-xs font-medium rounded-t transition-colors ${activeSection === t.id ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-800 hover:bg-slate-100"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {dashLoading && <div className="text-center py-6"><Loader2 className="h-5 w-5 animate-spin mx-auto text-slate-400" /></div>}

      {/* Job Runs Table */}
      {!dashLoading && activeSection === "runs" && dashboard?.job_runs && (
        <Card>
          <CardContent className="pt-4">
            {dashboard.job_runs.runs.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">No job runs recorded yet. Trigger a job above.</p>
            ) : (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-500 uppercase">
                    <tr><th className="px-3 py-2 text-left">Job</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-left">Window</th><th className="px-3 py-2 text-left">Started</th><th className="px-3 py-2 text-left">Duration</th></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {dashboard.job_runs.runs.map((r, i) => {
                      const started = r.started_at ? new Date(r.started_at) : null;
                      const completed = r.completed_at ? new Date(r.completed_at) : null;
                      const dur = started && completed ? `${((completed - started) / 1000).toFixed(1)}s` : "—";
                      return (
                        <tr key={i} className="hover:bg-slate-50">
                          <td className="px-3 py-2 font-mono font-medium text-slate-700">{(r.job_name || "").replace("rex_", "")}</td>
                          <td className={`px-3 py-2 font-semibold ${statusColor[r.status] || "text-slate-500"}`}>{r.status}</td>
                          <td className="px-3 py-2 text-slate-500">{r.run_window_key}</td>
                          <td className="px-3 py-2 text-slate-500">{started ? started.toLocaleString() : "—"}</td>
                          <td className="px-3 py-2 text-slate-500">{dur}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Flags Table */}
      {!dashLoading && activeSection === "flags" && dashboard?.flags && (
        <Card>
          <CardContent className="pt-4">
            {dashboard.flags.flags.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">No active flags. All clear.</p>
            ) : (
              <div className="space-y-2">
                {dashboard.flags.flags.map((f, i) => (
                  <div key={i} className={`border rounded-lg px-3 py-2.5 flex items-start gap-3 ${f.severity === "HIGH" ? "border-red-200 bg-red-50/50" : f.severity === "MED" ? "border-amber-200 bg-amber-50/50" : "border-slate-200"}`}>
                    <Badge className={`text-[10px] mt-0.5 ${severityColor[f.severity] || ""}`}>{f.severity}</Badge>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-slate-800 font-medium">{f.details}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{f.flag_type?.replace(/_/g, " ")} — {f.created_at ? new Date(f.created_at).toLocaleDateString() : ""}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Email Outbox */}
      {!dashLoading && activeSection === "outbox" && dashboard?.outbox && (
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center gap-4 mb-3">
              <Badge variant="outline" className="text-xs">Total: {dashboard.outbox.total}</Badge>
              <Badge className="bg-emerald-100 text-emerald-700 text-xs">Sent: {dashboard.outbox.sent}</Badge>
              <Badge className="bg-amber-100 text-amber-700 text-xs">Queued: {dashboard.outbox.queued}</Badge>
              {dashboard.outbox.failed > 0 && <Badge className="bg-red-100 text-red-700 text-xs">Failed: {dashboard.outbox.failed}</Badge>}
            </div>
            {dashboard.outbox.recent?.length > 0 ? (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-500 uppercase">
                    <tr><th className="px-3 py-2 text-left">Recipient</th><th className="px-3 py-2 text-left">Role</th><th className="px-3 py-2 text-left">Subject</th><th className="px-3 py-2 text-left">Status</th><th className="px-3 py-2 text-left">Date</th></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {dashboard.outbox.recent.map((e, i) => (
                      <tr key={i} className="hover:bg-slate-50">
                        <td className="px-3 py-2 text-slate-700">{e.recipient}</td>
                        <td className="px-3 py-2"><Badge variant="outline" className="text-[10px]">{e.role}</Badge></td>
                        <td className="px-3 py-2 text-slate-600 max-w-[200px] truncate">{e.subject}</td>
                        <td className="px-3 py-2"><Badge className={`text-[10px] ${outboxStatusColor[e.status] || ""}`}>{e.status}</Badge></td>
                        <td className="px-3 py-2 text-slate-500">{e.created_at ? new Date(e.created_at).toLocaleString() : ""}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-sm text-slate-400 text-center py-4">No emails in outbox yet.</p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Job Config (original toggle view) */}
      {activeSection === "config" && (
        <div className="space-y-2">
          <p className="text-sm text-slate-500 mb-2">Toggle Rex's automated background jobs on/off.</p>
          {jobs.map((job) => (
            <Card key={job.job_id} className={`transition-opacity ${job.enabled ? "" : "opacity-60"}`}>
              <CardContent className="py-3 flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm text-slate-900">{job.name}</span>
                    <Badge variant={job.enabled ? "default" : "secondary"} className="text-xs">{job.enabled ? "Active" : "Paused"}</Badge>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">{job.description}</p>
                  <p className="text-xs text-slate-400 mt-0.5 font-mono">{job.schedule}</p>
                </div>
                <Button data-testid={`toggle-job-${job.job_id}`} variant="ghost" size="sm" onClick={() => toggleJob(job.job_id)}
                  disabled={toggling === job.job_id} className={job.enabled ? "text-emerald-600" : "text-slate-400"}>
                  {job.enabled ? <ToggleRight className="h-6 w-6" /> : <ToggleLeft className="h-6 w-6" />}
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Tab: Authority Controls ──
function AuthorityControlsTab() {
  const [config, setConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editingRouting, setEditingRouting] = useState(false);
  const [newSector, setNewSector] = useState("");
  const [newAssigneeId, setNewAssigneeId] = useState("");
  const [newAssigneeLabel, setNewAssigneeLabel] = useState("");

  const fetchConfig = useCallback(() => {
    fetch(`${API}/admin-console/authority`, { credentials: "include" })
      .then((r) => r.json()).then(setConfig).catch(() => {});
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);

  const toggleFlag = async (flag) => {
    const newVal = !config.flags[flag];
    const res = await fetch(`${API}/admin-console/authority/flags`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      credentials: "include", body: JSON.stringify({ [flag]: newVal }),
    });
    if (res.ok) {
      setConfig((p) => ({ ...p, flags: { ...p.flags, [flag]: newVal } }));
      toast.success(`${flag} ${newVal ? "enabled" : "disabled"}`);
    }
  };

  const saveThresholds = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin-console/authority/thresholds`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(config.thresholds),
      });
      if (res.ok) toast.success("Thresholds updated");
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  const addRoutingEntry = () => {
    if (!newSector || !newAssigneeId || !newAssigneeLabel) return;
    setConfig((p) => ({
      ...p,
      routing: [...p.routing, { sector: newSector.toLowerCase(), assignee_id: newAssigneeId, assignee_label: newAssigneeLabel }],
    }));
    setNewSector(""); setNewAssigneeId(""); setNewAssigneeLabel("");
  };

  const removeRoutingEntry = (idx) => {
    setConfig((p) => ({ ...p, routing: p.routing.filter((_, i) => i !== idx) }));
  };

  const saveRouting = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/admin-console/authority/routing`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({
          entries: config.routing,
          default_assignee_id: config.default_route.assignee_id,
          default_assignee_label: config.default_route.assignee_label,
        }),
      });
      if (res.ok) { toast.success("Routing table saved"); setEditingRouting(false); }
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  if (!config) return <div className="flex justify-center p-8"><RefreshCw className="h-5 w-5 animate-spin text-slate-400" /></div>;

  const flagLabels = {
    auto_task_creation: { label: "Auto-Task Creation", desc: "Create Kanban tasks for high-scoring opportunities" },
    reactivation_tasks: { label: "Reactivation Tasks", desc: "Auto-create tasks for dormant client reactivation" },
    crm_mutation: { label: "CRM Mutation", desc: "Allow Rex to change CRM stage/status directly" },
    email_automation: { label: "Email Automation", desc: "Allow Rex to send outreach emails automatically" },
  };

  return (
    <div className="space-y-5" data-testid="authority-controls-tab">
      {/* Flags */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold uppercase tracking-wider text-slate-500">Controlled Authority Mode</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {Object.entries(flagLabels).map(([key, { label, desc }]) => (
            <div key={key} className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-slate-900">{label}</p>
                <p className="text-xs text-slate-500">{desc}</p>
              </div>
              <Switch
                data-testid={`flag-${key}`}
                checked={config.flags[key] || false}
                onCheckedChange={() => toggleFlag(key)}
              />
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Thresholds */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold uppercase tracking-wider text-slate-500">Scoring Thresholds</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {[
            { key: "task_creation_threshold", label: "Task Creation Score", unit: "/100" },
            { key: "mp_notification_threshold", label: "MP Notification Score", unit: "/100" },
            { key: "task_dedup_window_days", label: "De-duplication Window", unit: "days" },
            { key: "max_tasks_per_role_per_day", label: "Max Tasks / Role / Day", unit: "" },
          ].map(({ key, label, unit }) => (
            <div key={key} className="flex items-center gap-3">
              <Label className="text-sm text-slate-600 w-48">{label}</Label>
              <Input
                data-testid={`threshold-${key}`}
                type="number"
                value={config.thresholds[key]}
                onChange={(e) => setConfig((p) => ({ ...p, thresholds: { ...p.thresholds, [key]: parseInt(e.target.value) || 0 } }))}
                className="h-8 w-24 text-sm"
              />
              {unit && <span className="text-xs text-slate-400">{unit}</span>}
            </div>
          ))}
          <Button size="sm" onClick={saveThresholds} disabled={saving} data-testid="save-thresholds-btn">
            <Save className="h-3.5 w-3.5 mr-1.5" />{saving ? "Saving..." : "Save Thresholds"}
          </Button>
        </CardContent>
      </Card>

      {/* Routing Table */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold uppercase tracking-wider text-slate-500">Sector Routing Table</CardTitle>
            <Button variant="ghost" size="sm" onClick={() => setEditingRouting(!editingRouting)} className="h-7">
              {editingRouting ? "Cancel" : <><Pencil className="h-3.5 w-3.5 mr-1" />Edit</>}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="text-xs text-slate-500 mb-3">Default: <span className="font-medium text-slate-700">{config.default_route.assignee_label}</span></div>
          <div className="border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                <tr><th className="px-3 py-2 text-left">Sector</th><th className="px-3 py-2 text-left">Assigned To</th>{editingRouting && <th className="px-3 py-2 w-10"></th>}</tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {config.routing.map((r, i) => (
                  <tr key={i} className="hover:bg-slate-50">
                    <td className="px-3 py-2 text-slate-700">{r.sector}</td>
                    <td className="px-3 py-2 text-slate-600">{r.assignee_label}</td>
                    {editingRouting && (
                      <td className="px-3 py-2">
                        <button onClick={() => removeRoutingEntry(i)} className="text-red-400 hover:text-red-600"><Trash2 className="h-3.5 w-3.5" /></button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {editingRouting && (
            <div className="mt-3 space-y-2">
              <div className="flex gap-2">
                <Input placeholder="Sector" value={newSector} onChange={(e) => setNewSector(e.target.value)} className="h-8 text-sm" />
                <Input placeholder="Assignee ID" value={newAssigneeId} onChange={(e) => setNewAssigneeId(e.target.value)} className="h-8 text-sm" />
                <Input placeholder="Assignee Label" value={newAssigneeLabel} onChange={(e) => setNewAssigneeLabel(e.target.value)} className="h-8 text-sm" />
                <Button size="sm" variant="outline" onClick={addRoutingEntry} className="h-8 px-3"><Plus className="h-3.5 w-3.5" /></Button>
              </div>
              <Button size="sm" onClick={saveRouting} disabled={saving} data-testid="save-routing-btn">
                <Save className="h-3.5 w-3.5 mr-1.5" />{saving ? "Saving..." : "Save Routing"}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Geographic Exclusions */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold uppercase tracking-wider text-slate-500">Geographic Exclusions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-2">
            {(config.excluded_countries || []).map((c) => (
              <Badge key={c} variant="destructive" className="text-xs">{c}</Badge>
            ))}
          </div>
          <p className="text-xs text-slate-400 mt-2">Countries excluded from opportunity scoring and fact creation.</p>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Tab: Knowledge Base Admin ──
const CATEGORIES = ["Policy", "Process", "Client Intel", "Case Study", "Template", "General"];

function KnowledgeOverviewTab() {
  const [data, setData] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [websites, setWebsites] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadingZip, setUploadingZip] = useState(false);
  const [zipJob, setZipJob] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("General");
  const [newUrl, setNewUrl] = useState("");
  const [addingSite, setAddingSite] = useState(false);
  const [indexing, setIndexing] = useState(false);
  const [editingDocId, setEditingDocId] = useState(null);
  const [section, setSection] = useState("overview"); // overview | documents | websites
  const [sourceLink, setSourceLink] = useState("");

  const fetchAll = useCallback(() => {
    fetch(`${API}/admin-console/knowledge-overview`, { credentials: "include" })
      .then((r) => r.json()).then(setData).catch(() => {});
    fetch(`${API}/documents`, { credentials: "include" })
      .then((r) => r.json()).then(setDocuments).catch(() => {});
    fetch(`${API}/ask-rex/websites`, { credentials: "include" })
      .then((r) => r.json()).then(setWebsites).catch(() => setWebsites([]));
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // ── Document upload ──
  const [uploadProgress, setUploadProgress] = useState(0);
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validExts = [".pdf", ".docx", ".pptx", ".xlsx", ".xls", ".txt", ".md", ".csv", ".json", ".xml", ".html", ".htm", ".rtf"];
    const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();
    if (!validExts.includes(ext)) { toast.error("Unsupported file type"); return; }
    if (file.size > 50 * 1024 * 1024) { toast.error("File too large. Maximum size is 50MB"); return; }
    setUploading(true);
    setUploadProgress(0);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("category", selectedCategory);
    fd.append("source_link", sourceLink);
    try {
      // Use XMLHttpRequest for progress tracking
      const result = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open("POST", `${API}/documents/upload`);
        xhr.withCredentials = true;
        xhr.upload.onprogress = (e) => {
          if (e.lengthComputable) setUploadProgress(Math.round((e.loaded / e.total) * 100));
        };
        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(JSON.parse(xhr.responseText));
          } else {
            try { reject(new Error(JSON.parse(xhr.responseText).detail || `Upload failed (${xhr.status})`)); }
            catch { reject(new Error(`Upload failed with status ${xhr.status}`)); }
          }
        };
        xhr.onerror = () => reject(new Error("Network error — check connection"));
        xhr.ontimeout = () => reject(new Error("Upload timed out — try a smaller file"));
        xhr.timeout = 120000; // 2 minute timeout
        xhr.send(fd);
      });
      toast.success(`Uploaded ${file.name}`);
      fetchAll();
    } catch (err) {
      toast.error(err.message || "Upload failed");
    }
    finally { setUploading(false); setUploadProgress(0); e.target.value = ""; }
  };

  // ── ZIP upload ──
  const handleZipUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !file.name.toLowerCase().endsWith(".zip")) { toast.error("Please upload a ZIP file"); return; }
    setUploadingZip(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await fetch(`${API}/documents/upload-zip`, { method: "POST", credentials: "include", body: fd });
      if (res.ok) {
        const result = await res.json();
        toast.success("ZIP uploaded, processing...");
        setZipJob({ job_id: result.job_id, status: "processing", processed_files: 0, total_files: 0 });
        const poll = setInterval(async () => {
          const sr = await fetch(`${API}/documents/zip-status/${result.job_id}`, { credentials: "include" });
          if (sr.ok) {
            const job = await sr.json();
            setZipJob(job);
            if (job.status === "completed" || job.status === "failed") {
              clearInterval(poll);
              if (job.status === "completed") { toast.success("ZIP processed successfully"); fetchAll(); }
              else toast.error("ZIP processing failed");
            }
          }
        }, 2000);
      } else toast.error("ZIP upload failed");
    } catch { toast.error("ZIP upload failed"); }
    finally { setUploadingZip(false); e.target.value = ""; }
  };

  // ── Delete document ──
  const handleDelete = async (docId) => {
    if (!window.confirm("Delete this document?")) return;
    const res = await fetch(`${API}/documents/${docId}`, { method: "DELETE", credentials: "include" });
    if (res.ok) { setDocuments((p) => p.filter((d) => d.document_id !== docId)); toast.success("Deleted"); }
    else toast.error("Delete failed");
  };

  // ── Update category ──
  const handleUpdateCategory = async (docId, cat) => {
    const res = await fetch(`${API}/documents/${docId}`, { method: "PATCH", headers: { "Content-Type": "application/json" }, credentials: "include", body: JSON.stringify({ category: cat }) });
    if (res.ok) { setDocuments((p) => p.map((d) => d.document_id === docId ? { ...d, category: cat } : d)); setEditingDocId(null); toast.success("Updated"); }
  };

  // ── Add website ──
  const handleAddSite = async () => {
    if (!newUrl.trim()) return;
    setAddingSite(true);
    try {
      const res = await fetch(`${API}/ask-rex/websites`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ url: newUrl.trim() }),
      });
      if (res.ok) { toast.success("Website added, indexing started"); setNewUrl(""); fetchAll(); }
      else { const err = await res.json().catch(() => ({})); toast.error(err.detail || "Failed to add site"); }
    } catch { toast.error("Failed to add site"); }
    finally { setAddingSite(false); }
  };

  // ── Remove website ──
  const handleRemoveSite = async (id) => {
    const res = await fetch(`${API}/ask-rex/websites/${id}`, { method: "DELETE", credentials: "include" });
    if (res.ok) { setWebsites((p) => p.filter((w) => w.website_id !== id)); toast.success("Removed"); }
    else toast.error("Failed to remove");
  };

  // ── Reindex website ──
  const handleReindex = async (id) => {
    setIndexing(id);
    const res = await fetch(`${API}/ask-rex/websites/${id}/reindex`, { method: "POST", credentials: "include" });
    if (res.ok) { toast.success("Re-indexing started"); fetchAll(); }
    else toast.error("Re-index failed");
    setIndexing(false);
  };

  // ── Index default website ──
  const handleIndexDefault = async () => {
    setIndexing("default");
    const res = await fetch(`${API}/website/index`, { method: "POST", credentials: "include" });
    if (res.ok) { const r = await res.json(); toast.success(`Indexed ${r.pages_indexed} pages`); }
    else toast.error("Indexing failed");
    setIndexing(false);
  };

  const getCategoryColor = (cat) => {
    const m = { 
      "Policy": "bg-rose-100 text-rose-700", 
      "Process": "bg-blue-100 text-blue-700", 
      "Client Intel": "bg-amber-100 text-amber-700",
      "Case Study": "bg-emerald-100 text-emerald-700", 
      "Template": "bg-purple-100 text-purple-700",
      "General": "bg-slate-100 text-slate-700",
      Services: "bg-blue-100 text-blue-700", 
      "Case Studies": "bg-emerald-100 text-emerald-700", 
      "Company Info": "bg-purple-100 text-purple-700", 
      Other: "bg-slate-100 text-slate-700" 
    };
    return m[cat] || m.General;
  };

  if (!data) return <div className="flex justify-center p-8"><RefreshCw className="h-5 w-5 animate-spin text-slate-400" /></div>;

  const zoneLabels = { facts_sector_signals: "Sector Signals", facts_objections: "Objections", facts_case_studies: "Case Studies", facts_pricing: "Pricing", facts_win_loss_patterns: "Win/Loss Patterns", facts_delivery_patterns: "Delivery Patterns" };

  return (
    <div className="space-y-5" data-testid="knowledge-overview-tab">
      {/* Sub-nav */}
      <div className="flex gap-1 bg-slate-100 p-1 rounded-lg w-fit">
        {[
          { id: "overview", label: "Overview" },
          { id: "documents", label: `Documents (${documents.length})` },
          { id: "websites", label: `Websites (${websites.length || 0})` },
          { id: "ai_engine", label: "AI Engine" },
        ].map((s) => (
          <button key={s.id} onClick={() => setSection(s.id)} data-testid={`kb-section-${s.id}`}
            className={`px-3 py-1.5 text-sm rounded-md font-medium transition-colors ${section === s.id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}>
            {s.label}
          </button>
        ))}
      </div>

      {/* ═══ Overview Section ═══ */}
      {section === "overview" && (
        <>
          {/* Summary cards */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Card><CardContent className="pt-5 text-center"><p className="text-2xl font-bold text-amber-600">{data.totals.pending_review}</p><p className="text-xs text-slate-500 mt-1">Pending Review</p></CardContent></Card>
            <Card><CardContent className="pt-5 text-center"><p className="text-2xl font-bold text-emerald-600">{data.totals.approved}</p><p className="text-xs text-slate-500 mt-1">Approved Facts</p></CardContent></Card>
            <Card><CardContent className="pt-5 text-center"><p className="text-2xl font-bold text-blue-600">{data.canon.total}</p><p className="text-xs text-slate-500 mt-1">Canon Entries</p></CardContent></Card>
            <Card><CardContent className="pt-5 text-center"><p className="text-2xl font-bold text-slate-700">{data.knowledge_base_documents}</p><p className="text-xs text-slate-500 mt-1">KB Documents</p></CardContent></Card>
          </div>

          {/* Fact zones table */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold uppercase tracking-wider text-slate-500">Structured Fact Zones</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50 text-xs text-slate-500 uppercase">
                    <tr><th className="px-3 py-2 text-left">Zone</th><th className="px-3 py-2 text-center">Draft</th><th className="px-3 py-2 text-center">Proposed</th><th className="px-3 py-2 text-center">Approved</th><th className="px-3 py-2 text-center">Total</th></tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {Object.entries(data.fact_zones).map(([zone, counts]) => (
                      <tr key={zone} className="hover:bg-slate-50">
                        <td className="px-3 py-2 font-medium text-slate-700">{zoneLabels[zone] || zone}</td>
                        <td className="px-3 py-2 text-center">{counts.draft > 0 ? <Badge variant="secondary" className="text-xs">{counts.draft}</Badge> : <span className="text-slate-300">0</span>}</td>
                        <td className="px-3 py-2 text-center">{counts.proposed > 0 ? <Badge className="bg-amber-100 text-amber-700 text-xs">{counts.proposed}</Badge> : <span className="text-slate-300">0</span>}</td>
                        <td className="px-3 py-2 text-center">{counts.approved > 0 ? <Badge className="bg-emerald-100 text-emerald-700 text-xs">{counts.approved}</Badge> : <span className="text-slate-300">0</span>}</td>
                        <td className="px-3 py-2 text-center text-slate-500">{counts.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {data.raw_documents.pending_extraction > 0 && (
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="py-3 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-600" />
                <span className="text-sm text-amber-700">{data.raw_documents.pending_extraction} raw document(s) awaiting extraction</span>
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* ═══ Documents Section ═══ */}
      {section === "documents" && (
        <>
          {/* Upload row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Single file upload */}
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2"><Upload className="h-4 w-4" />Upload Document</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label className="text-xs text-slate-500">Category</Label>
                  <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                    <SelectTrigger data-testid="admin-upload-category" className="h-8 mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-slate-500">Source Link <span className="text-slate-400">(optional — OneDrive, Google Drive, etc.)</span></Label>
                  <Input
                    data-testid="admin-upload-source-link"
                    placeholder="https://drive.google.com/..."
                    value={sourceLink}
                    onChange={(e) => setSourceLink(e.target.value)}
                    className="h-8 mt-1 text-sm"
                  />
                </div>
                <div className="border-2 border-dashed border-slate-200 rounded-lg p-5 text-center">
                  <Upload className="h-6 w-6 mx-auto text-slate-400 mb-1.5" />
                  <p className="text-xs text-slate-500 mb-2">PDF, Word, Excel, PowerPoint, TXT, MD, CSV, JSON, XML, HTML</p>
                  <input type="file" accept=".pdf,.docx,.pptx,.xlsx,.xls,.txt,.md,.csv,.json,.xml,.html,.htm,.rtf" onChange={handleFileUpload} className="hidden" id="admin-doc-upload" />
                  <label htmlFor="admin-doc-upload">
                    <Button variant="outline" size="sm" disabled={uploading} asChild className="cursor-pointer" data-testid="admin-upload-btn">
                      <span>{uploading ? <><Loader2 className="h-3 w-3 animate-spin inline mr-1" />Uploading {uploadProgress}%</> : "Select File"}</span>
                    </Button>
                  </label>
                  {uploading && uploadProgress > 0 && (
                    <Progress value={uploadProgress} className="h-1.5 mt-2" />
                  )}
                </div>
              </CardContent>
            </Card>

            {/* ZIP upload */}
            <Card className="border-emerald-200">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold flex items-center gap-2 text-emerald-800"><FileArchive className="h-4 w-4" />Bulk Upload (ZIP)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-xs text-slate-600">Upload a ZIP with multiple documents. AI auto-categorises each file.</p>
                <div className="border-2 border-dashed border-emerald-200 rounded-lg p-5 text-center">
                  <FileArchive className="h-6 w-6 mx-auto text-emerald-500 mb-1.5" />
                  <input type="file" accept=".zip" onChange={handleZipUpload} className="hidden" id="admin-zip-upload" />
                  <label htmlFor="admin-zip-upload">
                    <Button variant="outline" size="sm" disabled={uploadingZip} asChild className="cursor-pointer border-emerald-300 text-emerald-700" data-testid="admin-zip-btn">
                      <span>{uploadingZip ? "Uploading..." : "Select ZIP"}</span>
                    </Button>
                  </label>
                </div>
                {zipJob?.status === "processing" && (
                  <div className="p-2.5 bg-blue-50 rounded-lg border border-blue-200 space-y-1.5">
                    <div className="flex items-center gap-2"><Loader2 className="h-3.5 w-3.5 animate-spin text-blue-600" /><span className="text-xs font-medium text-blue-700">Processing...</span></div>
                    <Progress value={zipJob.total_files > 0 ? (zipJob.processed_files / zipJob.total_files) * 100 : 0} className="h-1.5" />
                  </div>
                )}
                {zipJob?.status === "completed" && (
                  <div className="p-2.5 bg-emerald-50 rounded-lg border border-emerald-200 flex items-center justify-between">
                    <span className="text-xs text-emerald-700">Processing complete</span>
                    <Button size="sm" variant="ghost" className="text-xs h-6" onClick={() => setZipJob(null)}>Dismiss</Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Document list */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2"><FolderOpen className="h-4 w-4" />Uploaded Documents ({documents.length})</CardTitle>
            </CardHeader>
            <CardContent>
              {documents.length === 0 ? (
                <div className="text-center py-8 text-slate-400"><FileText className="h-10 w-10 mx-auto mb-2 text-slate-300" /><p className="text-sm">No documents uploaded yet</p></div>
              ) : (
                <div className="space-y-1.5">
                  {documents.map((doc) => (
                    <div key={doc.document_id} className="flex items-center justify-between p-2.5 border border-slate-100 rounded-lg hover:bg-slate-50" data-testid={`admin-doc-${doc.document_id}`}>
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <div className="w-8 h-8 bg-slate-100 rounded flex items-center justify-center flex-shrink-0"><FileText className="h-4 w-4 text-slate-500" /></div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-slate-900 truncate">{doc.name}</p>
                          {doc.summary && <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{doc.summary}</p>}
                          <div className="flex items-center gap-2 mt-0.5">
                            {editingDocId === doc.document_id ? (
                              <Select value={doc.category} onValueChange={(v) => handleUpdateCategory(doc.document_id, v)}>
                                <SelectTrigger className="h-5 text-xs w-28"><SelectValue /></SelectTrigger>
                                <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c} className="text-xs">{c}</SelectItem>)}</SelectContent>
                              </Select>
                            ) : (
                              <Badge variant="secondary" className={`text-xs cursor-pointer ${getCategoryColor(doc.category)}`} onClick={() => setEditingDocId(doc.document_id)}>
                                {doc.category}<Edit2 className="h-2.5 w-2.5 ml-1 opacity-50" />
                              </Badge>
                            )}
                            <span className="text-xs text-slate-400">{doc.file_size ? (doc.file_size < 1024 * 1024 ? `${(doc.file_size / 1024).toFixed(1)} KB` : `${(doc.file_size / (1024 * 1024)).toFixed(1)} MB`) : ""}</span>
                            {doc.source_link && (
                              <a href={doc.source_link} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline flex items-center gap-0.5">
                                <ExternalLink className="h-2.5 w-2.5" /> Source
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                      <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 h-7 w-7 p-0" onClick={() => handleDelete(doc.document_id)} data-testid={`admin-delete-doc-${doc.document_id}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {/* ═══ Websites Section ═══ */}
      {section === "websites" && (
        <>
          {/* Add website */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2"><Link2 className="h-4 w-4" />Add Website to Index</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="text-xs text-slate-500">Add external websites for Rex to crawl and use as knowledge context. Max 10 sites.</p>
              <div className="flex gap-2">
                <Input
                  data-testid="admin-add-url-input"
                  placeholder="https://example.com"
                  value={newUrl}
                  onChange={(e) => setNewUrl(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && handleAddSite()}
                  className="h-9 text-sm"
                />
                <Button onClick={handleAddSite} disabled={addingSite} size="sm" className="h-9" data-testid="admin-add-url-btn">
                  <Plus className="h-3.5 w-3.5 mr-1.5" />{addingSite ? "Adding..." : "Add Site"}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Default website index */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2"><Globe className="h-4 w-4" />RESILIGENT Website</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-slate-700">www.resiligent.com</p>
                  <p className="text-xs text-slate-500 mt-0.5">Home, About, Services, Contact</p>
                </div>
                <Button variant="outline" size="sm" onClick={handleIndexDefault} disabled={indexing === "default"} data-testid="admin-index-default-btn">
                  <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${indexing === "default" ? "animate-spin" : ""}`} />
                  {indexing === "default" ? "Indexing..." : "Re-index"}
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Indexed websites list */}
          {Array.isArray(websites) && websites.length > 0 && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-semibold uppercase tracking-wider text-slate-500">Indexed Sites ({websites.length})</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {websites.map((site) => (
                  <div key={site.website_id} className="flex items-center justify-between p-3 border border-slate-100 rounded-lg hover:bg-slate-50" data-testid={`admin-site-${site.website_id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-slate-900 truncate">{site.base_url}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <Badge variant={site.status === "completed" ? "default" : "secondary"} className={`text-xs ${site.status === "completed" ? "bg-emerald-100 text-emerald-700" : ""}`}>
                          {site.status === "completed" ? `${site.pages_indexed} pages` : site.status}
                        </Badge>
                        {site.indexed_at && <span className="text-xs text-slate-400">Last: {site.indexed_at.slice(0, 10)}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handleReindex(site.website_id)} disabled={indexing === site.website_id}>
                        <RefreshCw className={`h-3.5 w-3.5 ${indexing === site.website_id ? "animate-spin" : ""}`} />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-500 hover:text-red-700" onClick={() => handleRemoveSite(site.website_id)}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </>
      )}

      {/* ═══ AI Engine Section ═══ */}
      {section === "ai_engine" && (
        <AIEngineStatus />
      )}
    </div>
  );
}

// ── AI Engine Status ──
function AIEngineStatus() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(false);
  const [stressResult, setStressResult] = useState(null);
  const [stressLoading, setStressLoading] = useState(false);

  const checkHealth = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/health/llm`, { credentials: "include" });
      if (r.ok) setHealth(await r.json());
    } catch { /* ignore */ } finally { setLoading(false); }
  };

  const runStressTest = async () => {
    setStressLoading(true);
    try {
      const r = await fetch(`${API}/health/llm-stress-test`, { method: "POST", credentials: "include" });
      if (r.ok) setStressResult(await r.json());
    } catch { /* ignore */ } finally { setStressLoading(false); }
  };

  useEffect(() => { checkHealth(); }, []);

  return (
    <div className="space-y-4" data-testid="ai-engine-status">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-700">AI Engine Diagnostics</h3>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={checkHealth} disabled={loading} data-testid="ai-check-health">
            {loading ? "Checking..." : "Check Health"}
          </Button>
          <Button size="sm" variant="outline" onClick={runStressTest} disabled={stressLoading} data-testid="ai-stress-test">
            {stressLoading ? "Testing..." : "Stress Test"}
          </Button>
        </div>
      </div>

      {health && (
        <Card>
          <CardContent className="pt-4 space-y-3">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${health.llm_status === "OK" ? "bg-emerald-500" : "bg-red-500"}`} />
              <span className="text-sm font-medium">{health.llm_status === "OK" ? "AI Engine Connected" : "AI Engine Error"}</span>
              {health.proxy_url && <span className="text-xs text-slate-400">{health.proxy_url}</span>}
            </div>
            {health.key_preview && <p className="text-xs text-slate-400">Key: {health.key_preview}</p>}
            {health.error && <p className="text-xs text-red-600 bg-red-50 p-2 rounded">{health.error}</p>}

            {health.recent_errors && health.recent_errors.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-slate-500 mb-2">Recent AI Errors ({health.recent_errors.length})</p>
                <div className="space-y-1.5 max-h-60 overflow-y-auto">
                  {health.recent_errors.map((err, i) => (
                    <div key={i} className="text-xs bg-red-50 border border-red-100 p-2 rounded">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-red-700">{err.error_type}</span>
                        <span className="text-slate-400">{err.timestamp?.slice(0, 19)}</span>
                      </div>
                      <p className="text-red-600 mt-0.5 break-all">{err.error_detail}</p>
                      {err.message_preview && <p className="text-slate-400 mt-0.5 truncate">Message: {err.message_preview}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {health.recent_errors && health.recent_errors.length === 0 && (
              <p className="text-xs text-emerald-600">No recent AI errors recorded</p>
            )}
          </CardContent>
        </Card>
      )}

      {stressResult && (
        <Card>
          <CardContent className="pt-4">
            <p className="text-sm font-medium mb-2">
              Stress Test: <span className={stressResult.overall === "PASS" ? "text-emerald-600" : stressResult.overall === "RECOVERY_WORKS" ? "text-amber-600" : "text-red-600"}>{stressResult.overall}</span>
            </p>
            <div className="space-y-1">
              {stressResult.steps?.map((step, i) => (
                <div key={i} className="flex items-center gap-2 text-xs">
                  <span className={`w-2 h-2 rounded-full ${step.status === "OK" ? "bg-emerald-500" : step.status === "SKIP" ? "bg-slate-300" : "bg-red-500"}`} />
                  <span className="font-medium text-slate-600">{step.step}</span>
                  <span className="text-slate-400">{step.detail?.slice(0, 80)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── Tab: Risk & Overrides ──
function RiskOverridesTab() {
  const [data, setData] = useState(null);

  useEffect(() => {
    fetch(`${API}/admin-console/risk-overview`, { credentials: "include" })
      .then((r) => r.json()).then(setData).catch(() => {});
  }, []);

  const handleReview = async (alertId) => {
    const res = await fetch(`${API}/admin-console/alerts/${alertId}/review`, {
      method: "PUT", credentials: "include",
    });
    if (res.ok) {
      setData((p) => ({
        ...p,
        alerts: {
          ...p.alerts,
          active_count: p.alerts.active_count - 1,
          entries: p.alerts.entries.filter((a) => a.alert_id !== alertId),
        },
      }));
      toast.success("Alert marked as reviewed");
    }
  };

  if (!data) return <div className="flex justify-center p-8"><RefreshCw className="h-5 w-5 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-5" data-testid="risk-overrides-tab">
      {/* Override stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-5 text-center">
            <p className="text-2xl font-bold text-slate-900">{data.overrides.this_week}</p>
            <p className="text-xs text-slate-500 mt-1">Overrides This Week</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 text-center">
            <p className="text-2xl font-bold text-slate-700">{data.overrides.this_month}</p>
            <p className="text-xs text-slate-500 mt-1">Overrides This Month</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-5 text-center">
            <p className="text-2xl font-bold text-slate-500">{data.overrides.total}</p>
            <p className="text-xs text-slate-500 mt-1">Total Overrides</p>
          </CardContent>
        </Card>
      </div>

      {/* Active alerts */}
      {data.alerts.active_count > 0 && (
        <Card className="border-red-200">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold text-red-700 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />Active Alerts ({data.alerts.active_count})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {data.alerts.entries.map((alert) => (
              <div key={alert.alert_id} className="flex items-center justify-between p-3 bg-red-50 rounded-lg">
                <div>
                  <p className="text-sm font-medium text-red-800">{alert.message || alert.alert_type || "Override threshold breach"}</p>
                  <p className="text-xs text-red-600 mt-0.5">{alert.created_at?.slice(0, 16)}</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => handleReview(alert.alert_id)} data-testid={`review-alert-${alert.alert_id}`}>
                  <Check className="h-3.5 w-3.5 mr-1" />Review
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
      {data.alerts.active_count === 0 && (
        <Card className="border-emerald-200 bg-emerald-50">
          <CardContent className="py-4 flex items-center gap-2">
            <Check className="h-4 w-4 text-emerald-600" />
            <span className="text-sm text-emerald-700">No active alerts</span>
          </CardContent>
        </Card>
      )}

      {/* Recent overrides */}
      {data.overrides.recent.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold uppercase tracking-wider text-slate-500">Recent Overrides</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.overrides.recent.map((o, i) => (
                <div key={i} className="p-3 bg-slate-50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-slate-700">{o.action_type || "Unknown"}</span>
                    <Badge variant={o.risk_level === "HIGH" ? "destructive" : "secondary"} className="text-xs">{o.risk_level}</Badge>
                  </div>
                  <p className="text-xs text-slate-500 mt-1">{o.justification || "No justification"}</p>
                  <p className="text-xs text-slate-400 mt-1">{o.timestamp?.slice(0, 16)} — {o.user_id}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── Tab: Integrations (incorporating old Settings) ──
function IntegrationsTab({ user }) {
  const [signature, setSignature] = useState(user?.signature_html || "");
  const [savingSignature, setSavingSignature] = useState(false);
  const [gammaTemplateId, setGammaTemplateId] = useState("");
  const [gammaThemeId, setGammaThemeId] = useState("");
  const [gammaEmail, setGammaEmail] = useState("");
  const [gammaPassword, setGammaPassword] = useState("");
  const [gammaPasswordSet, setGammaPasswordSet] = useState(false);
  const [showGammaPassword, setShowGammaPassword] = useState(false);
  const [savingGamma, setSavingGamma] = useState(false);
  const [bufHealth, setBufHealth] = useState(null);
  const [bufLoading, setBufLoading] = useState(false);
  const [bufResetting, setBufResetting] = useState(false);
  const navigate = useNavigate();

  const loadBufferHealth = useCallback(async () => {
    setBufLoading(true);
    try {
      const r = await fetch(`${API}/buffer/health`, { credentials: "include" });
      const d = await r.json().catch(() => ({}));
      setBufHealth(d);
    } catch { setBufHealth({ ok: false, detail: "Could not reach server" }); }
    finally { setBufLoading(false); }
  }, []);

  const resetBuffer = async () => {
    setBufResetting(true);
    try {
      const r = await fetch(`${API}/buffer/reset-poll-state`, {
        method: "POST", credentials: "include",
      });
      const d = await r.json().catch(() => ({}));
      if (r.ok) {
        toast.success(`Buffer poll state reset — drained ${d.backlog_drained ?? 0} stuck items. It will reconnect at Buffer's next daily reset.`);
        loadBufferHealth();
      } else {
        toast.error(d.detail || "Reset failed");
      }
    } catch { toast.error("Reset failed"); }
    finally { setBufResetting(false); }
  };

  useEffect(() => { loadBufferHealth(); }, [loadBufferHealth]);

  useEffect(() => {
    fetch(`${API}/settings/gamma`, { credentials: "include" })
      .then((r) => r.json()).then((d) => {
        setGammaTemplateId(d.gamma_template_id || "");
        setGammaThemeId(d.gamma_theme_id || "");
        setGammaEmail(d.gamma_email || "");
        setGammaPasswordSet(d.gamma_password_set || false);
      }).catch(() => {});
  }, []);

  const handleSaveSignature = async () => {
    setSavingSignature(true);
    try {
      const res = await fetch(`${API}/auth/signature`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ signature_html: signature }),
      });
      if (res.ok) toast.success("Signature saved");
      else toast.error("Failed to save signature");
    } catch { toast.error("Failed to save signature"); }
    finally { setSavingSignature(false); }
  };

  const handleSaveGamma = async () => {
    setSavingGamma(true);
    try {
      const payload = { gamma_template_id: gammaTemplateId, gamma_theme_id: gammaThemeId, gamma_email: gammaEmail };
      if (gammaPassword) payload.gamma_password = gammaPassword;
      const res = await fetch(`${API}/settings/gamma`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify(payload),
      });
      if (res.ok) { toast.success("Gamma settings saved"); setGammaPassword(""); setGammaPasswordSet(true); }
      else toast.error("Failed to save Gamma settings");
    } catch { toast.error("Failed to save Gamma settings"); }
    finally { setSavingGamma(false); }
  };

  return (
    <div className="space-y-5" data-testid="integrations-tab">
      {/* Buffer Connection */}
      {(() => {
        const ok = bufHealth?.ok === true;
        const rateLimited = !ok && /too many requests|rate.?limit/i.test(bufHealth?.detail || "");
        const statusLabel = bufLoading ? "Checking…" : ok ? "Connected" : rateLimited ? "Rate-limited" : (bufHealth?.configured === false ? "Not configured" : "Disconnected");
        const cardBorder = ok ? "border-emerald-200" : rateLimited ? "border-amber-200" : "border-red-200";
        const dot = ok ? "bg-emerald-500" : rateLimited ? "bg-amber-500" : "bg-red-500";
        return (
          <Card className={cardBorder} data-testid="buffer-connection-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-800">
                <Send className="h-4 w-4" />Buffer Connection (publishing)
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-2">
                  <span className={`inline-block h-2.5 w-2.5 rounded-full ${dot}`} />
                  <span className="text-sm font-semibold text-slate-800" data-testid="buffer-status-label">{statusLabel}</span>
                  {!ok && bufHealth?.detail && (
                    <span className="text-xs text-slate-500 truncate max-w-[420px]" data-testid="buffer-status-detail">— {bufHealth.detail}</span>
                  )}
                </div>
                <Button variant="outline" size="sm" onClick={loadBufferHealth} disabled={bufLoading} data-testid="buffer-recheck-btn" className="h-8">
                  <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${bufLoading ? "animate-spin" : ""}`} />Re-check
                </Button>
              </div>
              {rateLimited && (
                <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2.5" data-testid="buffer-ratelimit-note">
                  Buffer is throttling the token (its API allows ~250 requests/day). Click <strong>Reset Buffer connection</strong> to drain any stuck publish backlog and clear the throttle state — it will reconnect on its own at Buffer's next daily reset.
                </div>
              )}
              <div className="flex items-center gap-3">
                <Button onClick={resetBuffer} disabled={bufResetting} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="buffer-reset-btn">
                  {bufResetting ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Activity className="h-4 w-4 mr-2" />}
                  Reset Buffer connection
                </Button>
                <span className="text-xs text-slate-400">Drains the stuck publish backlog &amp; clears the rate-limit cooldown.</span>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Gamma Integration */}
      <Card className="border-violet-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2 text-violet-800">
            <FileText className="h-4 w-4" />Gamma.app Integration
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs text-slate-500">Gamma Email</Label>
              <Input data-testid="gamma-email-input" type="email" value={gammaEmail} onChange={(e) => setGammaEmail(e.target.value)} placeholder="proposals@yourcompany.com" className="mt-1 h-9 text-sm" />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Gamma Password {gammaPasswordSet && <span className="text-emerald-600 text-xs ml-1">(saved)</span>}</Label>
              <div className="relative mt-1">
                <Input data-testid="gamma-password-input" type={showGammaPassword ? "text" : "password"} value={gammaPassword} onChange={(e) => setGammaPassword(e.target.value)} placeholder={gammaPasswordSet ? "••••••••" : "Enter password"} className="h-9 text-sm pr-9" />
                <button type="button" onClick={() => setShowGammaPassword(!showGammaPassword)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                  {showGammaPassword ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                </button>
              </div>
            </div>
          </div>
          <div>
            <Label className="text-xs text-slate-500">Gamma Theme ID</Label>
            <Input data-testid="gamma-theme-input" value={gammaThemeId} onChange={(e) => setGammaThemeId(e.target.value)} placeholder="e.g., fu1ozzetea5s7r2" className="mt-1 h-9 text-sm font-mono" />
          </div>
          <div>
            <Label className="text-xs text-slate-400">Template ID (Optional)</Label>
            <Input value={gammaTemplateId} onChange={(e) => setGammaTemplateId(e.target.value)} placeholder="e.g., g_t5ah34juczhxp3g" className="mt-1 h-9 text-sm font-mono text-slate-400" />
          </div>
          <div className="flex items-center justify-between">
            <a href="https://gamma.app/create" target="_blank" rel="noopener noreferrer" className="text-xs text-violet-600 hover:underline flex items-center gap-1">
              Open Gamma.app <ExternalLink className="h-3 w-3" />
            </a>
            <Button size="sm" onClick={handleSaveGamma} disabled={savingGamma} className="bg-violet-600 hover:bg-violet-700" data-testid="save-gamma-btn">
              <Save className="h-3.5 w-3.5 mr-1.5" />{savingGamma ? "Saving..." : "Save Gamma"}
            </Button>
          </div>
          {gammaThemeId && <div className="p-2.5 bg-emerald-50 rounded-lg text-xs text-emerald-700 flex items-center gap-1.5"><Check className="h-3.5 w-3.5" />Gamma configured</div>}
        </CardContent>
      </Card>

      {/* Email Signature */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
            <Signature className="h-4 w-4" />Email Signature
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Textarea data-testid="signature-textarea" value={signature} onChange={(e) => setSignature(e.target.value)} placeholder="Enter your email signature (HTML)" rows={5} className="font-mono text-xs" />
          <div className="flex justify-end">
            <Button size="sm" onClick={handleSaveSignature} disabled={savingSignature} data-testid="save-signature-btn">
              <Save className="h-3.5 w-3.5 mr-1.5" />{savingSignature ? "Saving..." : "Save Signature"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* OneDrive */}
      <OneDriveSettings />

      {/* Security */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2 text-slate-700">
            <Shield className="h-4 w-4" />Security
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => { fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" }).finally(() => navigate("/login")); }} data-testid="settings-logout-btn">
            <LogOut className="h-3.5 w-3.5 mr-1.5" />Sign Out
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Tab: System Health ──
function SystemHealthTab() {
  const [data, setData] = useState(null);
  const [llmStatus, setLlmStatus] = useState(null);
  const [llmTesting, setLlmTesting] = useState(false);

  useEffect(() => {
    fetch(`${API}/admin-console/system-health`, { credentials: "include" })
      .then((r) => r.json()).then(setData).catch(() => {});
  }, []);

  const testLlm = async () => {
    setLlmTesting(true);
    setLlmStatus(null);
    try {
      const res = await fetch(`${API}/health/llm`, { credentials: "include" });
      const d = await res.json();
      setLlmStatus(d);
    } catch {
      setLlmStatus({ llm_status: "FAILED", error: "Could not reach health endpoint" });
    }
    setLlmTesting(false);
  };

  if (!data) return <div className="flex justify-center p-8"><RefreshCw className="h-5 w-5 animate-spin text-slate-400" /></div>;

  return (
    <div className="space-y-5" data-testid="system-health-tab">
      {/* Live Platform Health (relocated from Dashboard — May 2026) */}
      <PlatformHealthWidget />

      {/* AI Engine Status */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold uppercase tracking-wider text-slate-500">AI Engine (Rex)</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-slate-600">Test the LLM connection, API key validity, and response capability.</p>
              {llmStatus && (
                <div className={`mt-3 p-3 rounded-lg text-sm ${llmStatus.llm_status === "OK" ? "bg-emerald-50 border border-emerald-200" : "bg-red-50 border border-red-200"}`} data-testid="llm-test-result">
                  <div className="flex items-center gap-2">
                    {llmStatus.llm_status === "OK" ? (
                      <Check className="w-4 h-4 text-emerald-600" />
                    ) : (
                      <AlertTriangle className="w-4 h-4 text-red-600" />
                    )}
                    <span className={`font-medium ${llmStatus.llm_status === "OK" ? "text-emerald-700" : "text-red-700"}`}>
                      {llmStatus.llm_status === "OK" ? "AI Engine is healthy" : "AI Engine is not responding"}
                    </span>
                  </div>
                  <div className="mt-2 space-y-1 text-xs text-slate-600">
                    <p>Key: {llmStatus.key_configured ? `${llmStatus.key_preview}` : "NOT CONFIGURED"}</p>
                    {llmStatus.llm_status === "OK" && <p>Response: "{llmStatus.response}"</p>}
                    {llmStatus.error && <p className="text-red-600 font-mono">{llmStatus.error}</p>}
                  </div>
                </div>
              )}
            </div>
            <Button
              onClick={testLlm}
              disabled={llmTesting}
              variant="outline"
              className="ml-4 shrink-0"
              data-testid="test-llm-btn"
            >
              {llmTesting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Activity className="w-4 h-4 mr-2" />}
              {llmTesting ? "Testing..." : "Test AI Engine"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Users */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold uppercase tracking-wider text-slate-500">Users & Sessions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div><p className="text-2xl font-bold text-slate-900">{data.users.total}</p><p className="text-xs text-slate-500">Total Users</p></div>
            <div><p className="text-2xl font-bold text-emerald-600">{data.users.active}</p><p className="text-xs text-slate-500">Active Users</p></div>
            <div><p className="text-2xl font-bold text-blue-600">{data.users.sessions}</p><p className="text-xs text-slate-500">Active Sessions</p></div>
          </div>
        </CardContent>
      </Card>

      {/* Integrations Status */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold uppercase tracking-wider text-slate-500">Integration Status</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {Object.entries(data.integrations).map(([key, info]) => (
            <div key={key} className="flex items-center justify-between py-2">
              <span className="text-sm text-slate-700">{info.label}</span>
              <Badge variant={info.configured ? "default" : "secondary"} className={`text-xs ${info.configured ? "bg-emerald-100 text-emerald-700" : ""}`}>
                {info.configured ? "Connected" : "Not Configured"}
              </Badge>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Data Stats */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold uppercase tracking-wider text-slate-500">Data Overview</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div><p className="text-2xl font-bold text-slate-900">{data.data.clients}</p><p className="text-xs text-slate-500">Clients</p></div>
            <div><p className="text-2xl font-bold text-slate-700">{data.data.proposals}</p><p className="text-xs text-slate-500">Proposals</p></div>
            <div><p className="text-2xl font-bold text-slate-500">{data.data.engagements}</p><p className="text-xs text-slate-500">Engagements</p></div>
          </div>
        </CardContent>
      </Card>

      {/* Version */}
      <Card>
        <CardContent className="py-4 text-center text-sm text-slate-500">
          <p className="font-medium text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>REX — RESILIGENT Engine for eXcellence</p>
          <p className="mt-1">Version 2.0.0</p>
        </CardContent>
      </Card>
    </div>
  );
}

// ── Tab: Data Management (Excel Import/Export) ──
// Sub-component: Copy data from another environment (Preview → Prod seed)
function CopyFromPreviewCard() {
  const STORAGE_KEY = "rex_migration_source";
  const [sourceUrl, setSourceUrl] = useState(() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY) || "{}").source_url || ""; } catch { return ""; }
  });
  const [sourceKey, setSourceKey] = useState("");
  const [remember, setRemember] = useState(true);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const runMigration = async () => {
    if (!sourceUrl.trim() || !sourceKey.trim()) {
      toast.error("Enter both the source URL and migration key");
      return;
    }
    if (!window.confirm(
      `This will pull ALL collections from:\n\n${sourceUrl}\n\n…and insert any missing documents into THIS environment.\n\nIt is non-destructive — existing documents are skipped (matched by client_id / email / etc.). Continue?`
    )) return;

    setRunning(true);
    setResult(null);
    try {
      if (remember) {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ source_url: sourceUrl.trim() }));
      }
      const r = await fetch(`${API}/admin/migrate-from-source`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          source_url: sourceUrl.trim(),
          source_key: sourceKey.trim(),
        }),
      });
      const d = await r.json();
      if (r.ok) {
        setResult({ ok: true, data: d });
        toast.success(`Imported ${d.total_inserted} docs across ${d.fetched_collections} collections`);
      } else {
        setResult({ ok: false, error: d.detail || "Migration failed" });
        toast.error(d.detail || "Migration failed");
      }
    } catch (e) {
      setResult({ ok: false, error: e.message || "Network error" });
      toast.error("Network error");
    }
    setRunning(false);
  };

  return (
    <Card data-testid="copy-from-preview-card" className="border-emerald-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm font-semibold uppercase tracking-wider text-emerald-700 flex items-center gap-2">
          <Download className="h-4 w-4" /> Copy data from another environment
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-slate-600">
          One-click seed from another deployment (e.g. Preview → Production) when this environment's MongoDB is empty.
          Fetches every collection from the source's <span className="font-mono text-xs">/api/admin/export-data</span> and inserts only new documents here (idempotent — safe to re-run).
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs text-slate-500">Source URL</Label>
            <Input
              value={sourceUrl}
              onChange={(e) => setSourceUrl(e.target.value)}
              placeholder="https://<preview-host>.emergentagent.com"
              data-testid="migration-source-url"
              disabled={running}
            />
          </div>
          <div>
            <Label className="text-xs text-slate-500">Source migration key</Label>
            <Input
              type="password"
              value={sourceKey}
              onChange={(e) => setSourceKey(e.target.value)}
              placeholder="MIGRATION_SECRET from source env"
              data-testid="migration-source-key"
              disabled={running}
            />
          </div>
        </div>

        <label className="inline-flex items-center gap-2 text-xs text-slate-500">
          <input
            type="checkbox"
            checked={remember}
            onChange={(e) => setRemember(e.target.checked)}
            data-testid="migration-remember-url"
          />
          Remember URL in this browser (key is never stored)
        </label>

        <div>
          <Button
            onClick={runMigration}
            disabled={running}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="migration-run-btn"
          >
            {running ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
            {running ? "Migrating…" : "Copy data now"}
          </Button>
        </div>

        {result?.ok && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs" data-testid="migration-result-success">
            <p className="font-semibold text-emerald-800 mb-2 flex items-center gap-1.5">
              <Check className="h-4 w-4" /> Migration complete
            </p>
            <div className="grid grid-cols-2 gap-1 text-emerald-900 mb-2">
              <div className="flex justify-between"><span>Collections fetched</span><span className="font-mono font-semibold">{result.data.fetched_collections}</span></div>
              <div className="flex justify-between"><span>Docs fetched</span><span className="font-mono font-semibold">{result.data.fetched_docs}</span></div>
              <div className="flex justify-between"><span>Inserted</span><span className="font-mono font-semibold">{result.data.total_inserted}</span></div>
              <div className="flex justify-between"><span>Skipped (existed)</span><span className="font-mono font-semibold">{result.data.total_skipped}</span></div>
              {typeof result.data.total_errors === "number" && result.data.total_errors > 0 && (
                <div className="flex justify-between col-span-2 text-amber-700"><span>Errors (non-fatal)</span><span className="font-mono font-semibold">{result.data.total_errors}</span></div>
              )}
            </div>
            {result.data.results && Object.keys(result.data.results).length > 0 && (
              <details>
                <summary className="cursor-pointer text-emerald-700 font-medium">Per-collection breakdown</summary>
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-0.5 max-h-60 overflow-y-auto">
                  {Object.entries(result.data.results).map(([k, v]) => (
                    <div key={k} className="flex justify-between bg-white border border-emerald-100 rounded px-2 py-1">
                      <span className="font-mono text-[11px] truncate">{k}</span>
                      <span className="text-[11px] text-emerald-700">+{v.inserted} / skip {v.skipped}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        )}

        {result && !result.ok && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700" data-testid="migration-result-error">
            {result.error}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ── Tab: Data Management (Excel Import/Export) ──
function DataManagementTab() {
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [hygPreview, setHygPreview] = useState(null);
  const [hygResult, setHygResult] = useState(null);
  const [hygLoading, setHygLoading] = useState(false);
  const [hygRunning, setHygRunning] = useState(false);

  const fetchHygPreview = async () => {
    setHygLoading(true);
    try {
      const r = await fetch(`${API}/admin/data/preview`, { credentials: "include" });
      const d = await r.json();
      if (r.ok) {
        setHygPreview(d);
      } else {
        toast.error(d.detail || "Failed to load preview");
        setHygPreview(null);
      }
    } catch {
      toast.error("Failed to load preview");
    }
    setHygLoading(false);
  };

  const runHygiene = async () => {
    if (!window.confirm(
      "This will:\n• Soft-delete TEST_/UAT engagements, programmes and content cards\n• Archive Content Studio entries in action_recommendations\n• Migrate Mongo content_drafts into the v3 Amplify Planner\n\nAll operations are idempotent and reversible. Continue?"
    )) return;
    setHygRunning(true);
    setHygResult(null);
    try {
      const r = await fetch(`${API}/admin/data/cleanup-and-migrate`, {
        method: "POST",
        credentials: "include",
      });
      const d = await r.json();
      if (r.ok) {
        setHygResult({ ok: true, stats: d.stats });
        toast.success("Data hygiene complete");
        // Refresh preview to show post-run state
        fetchHygPreview();
      } else {
        setHygResult({ ok: false, error: d.detail || "Failed" });
        toast.error(d.detail || "Failed");
      }
    } catch {
      setHygResult({ ok: false, error: "Network error" });
      toast.error("Network error");
    }
    setHygRunning(false);
  };

  // ─── Purge known test residue (Feb 2026, REX-105) ───
  const [purgeRunning, setPurgeRunning] = useState(false);
  const [purgeResult, setPurgeResult] = useState(null);
  const runPurge = async (dryRun = false) => {
    if (!dryRun && !window.confirm(
      "This will hard-delete content cards / Mongo items / opportunities whose title matches well-known test patterns:\n\n• 'XKCD'\n• 'Quantum chromodynamics'\n• '[TEST]'\n• 'Acme INV-'\n• 'Beta Bank'\n• 'Pytest'\n• 'phase7-test'\n• 'TEST_AMPGATE'\n\nThis is irreversible. Run a Dry Run first to see counts. Continue?"
    )) return;
    setPurgeRunning(true);
    setPurgeResult(null);
    try {
      const url = `${API}/admin/data/purge-test-residue${dryRun ? "?dry_run=1" : ""}`;
      const r = await fetch(url, { method: "POST", credentials: "include" });
      const d = await r.json();
      if (r.ok) {
        setPurgeResult(d);
        const total = Object.values(d.purged || {}).reduce((a, b) => a + b, 0);
        toast.success(dryRun ? `Dry run: ${total} matching rows found` : `Purged ${total} rows`);
      } else {
        toast.error(d.detail || "Failed");
      }
    } catch {
      toast.error("Network error");
    }
    setPurgeRunning(false);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await fetch(`${API}/clients/export-excel`, { credentials: "include" });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `resiligent_clients_${new Date().toISOString().split("T")[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        toast.success("Excel exported successfully");
      } else {
        toast.error("Export failed");
      }
    } catch { toast.error("Export failed"); }
    setExporting(false);
  };

  const handleImportReplace = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const response = await fetch(`${API}/clients/import-replace`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
      const data = await response.json();
      if (response.ok) {
        setImportResult({ success: true, message: `Imported ${data.imported_count} clients (replaced ${data.deleted_count} old records)` });
        toast.success("Import successful");
      } else {
        setImportResult({ success: false, message: data.detail || "Import failed" });
        toast.error(data.detail || "Import failed");
      }
    } catch { toast.error("Import failed"); setImportResult({ success: false, message: "Import failed" }); }
    setImporting(false);
    e.target.value = "";
  };

  return (
    <div className="space-y-5" data-testid="data-management-tab">
      <p className="text-sm text-slate-500">Export client data, run platform-wide data hygiene, or replace clients with an Excel upload.</p>

      <CopyFromPreviewCard />

      {/* REX-105 — Purge known test residue */}
      <Card data-testid="purge-test-residue-card" className="border-rose-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold uppercase tracking-wider text-rose-700 flex items-center gap-2">
            <AlertTriangle className="h-4 w-4" /> Purge test residue
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-slate-600">
            Hard-deletes content cards / Mongo items / opportunities matching test patterns
            (<span className="font-mono text-xs">XKCD</span>,{" "}
            <span className="font-mono text-xs">Quantum chromodynamics</span>,{" "}
            <span className="font-mono text-xs">[TEST]</span>,{" "}
            <span className="font-mono text-xs">Acme INV-</span>, etc.).
            Run <strong>Dry Run</strong> first to see counts before purging.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={() => runPurge(true)}
              disabled={purgeRunning}
              data-testid="purge-dryrun-btn"
            >
              {purgeRunning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
              Dry Run
            </Button>
            <Button
              onClick={() => runPurge(false)}
              disabled={purgeRunning}
              className="bg-rose-600 hover:bg-rose-700 text-white"
              data-testid="purge-run-btn"
            >
              {purgeRunning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
              Purge now
            </Button>
          </div>
          {purgeResult && (
            <div className={`text-xs rounded p-3 mt-2 border ${purgeResult.dry_run ? "bg-amber-50 border-amber-200 text-amber-800" : "bg-emerald-50 border-emerald-200 text-emerald-800"}`} data-testid="purge-result">
              <p className="font-semibold mb-1">{purgeResult.dry_run ? "Dry run results" : "Purge results"}</p>
              <ul className="space-y-0.5">
                {Object.entries(purgeResult.purged || {}).map(([k, v]) => (
                  <li key={k}><span className="font-mono">{k}:</span> {v} row{v === 1 ? "" : "s"}</li>
                ))}
              </ul>
            </div>
          )}
        </CardContent>
      </Card>

      <Card data-testid="data-hygiene-card" className="border-indigo-200">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold uppercase tracking-wider text-indigo-700 flex items-center gap-2">
            <Shield className="h-4 w-4" /> Platform Data Hygiene
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-slate-600">
            Cleans test/UAT pollution from engagements & programmes, archives stale Content Studio entries from the Morning Intelligence feed, and migrates legacy Content Studio drafts into the v3 Amplify Planner.
            Idempotent and soft-delete only — safe to re-run.
          </p>

          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              onClick={fetchHygPreview}
              disabled={hygLoading || hygRunning}
              data-testid="hyg-preview-btn"
            >
              {hygLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
              Preview impact
            </Button>
            <Button
              onClick={runHygiene}
              disabled={hygRunning || hygLoading}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="hyg-run-btn"
            >
              {hygRunning ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
              {hygRunning ? "Running..." : "Run cleanup & migration"}
            </Button>
          </div>

          {hygPreview && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs" data-testid="hyg-preview-grid">
              {[
                ["Test engagements to purge", hygPreview.pg_test_engagements_to_purge],
                ["Test programmes to purge", hygPreview.pg_test_programmes_to_purge],
                ["Test content cards to purge", hygPreview.pg_test_content_cards_to_purge],
                ["Content Studio recs to archive", hygPreview.mongo_action_recommendations_to_archive],
                ["Drafts to migrate → Planner", hygPreview.mongo_drafts_to_migrate_into_pg],
                ["Content drafts in Mongo (total)", hygPreview.mongo_content_drafts_total],
                ["Active content cards (Postgres)", hygPreview.pg_active_content_cards_total],
              ].map(([label, val]) => (
                <div
                  key={label}
                  className="flex justify-between items-center bg-slate-50 border border-slate-200 rounded px-3 py-2"
                >
                  <span className="text-slate-600">{label}</span>
                  <span className={`font-mono font-semibold ${val > 0 ? "text-indigo-700" : "text-slate-400"}`}>
                    {val}
                  </span>
                </div>
              ))}
            </div>
          )}

          {hygResult && hygResult.ok && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm" data-testid="hyg-result-success">
              <p className="font-semibold text-emerald-800 mb-2 flex items-center gap-1.5">
                <Check className="h-4 w-4" /> Cleanup complete
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1 text-xs text-emerald-900">
                {Object.entries(hygResult.stats).map(([k, v]) => (
                  <div key={k} className="flex justify-between">
                    <span className="text-emerald-700">{k.replace(/_/g, " ")}</span>
                    <span className="font-mono font-semibold">{v}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {hygResult && !hygResult.ok && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700" data-testid="hyg-result-error">
              {hygResult.error}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold uppercase tracking-wider text-slate-500">Export Client Data</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-600 mb-3">Download all client records as an Excel (.xlsx) file for review or offline editing.</p>
          <Button onClick={handleExport} disabled={exporting} data-testid="admin-export-excel-btn">
            {exporting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Download className="w-4 h-4 mr-2" />}
            {exporting ? "Exporting..." : "Export All Clients"}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold uppercase tracking-wider text-slate-500">Import & Replace</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
            <p className="text-sm text-amber-800">This will <strong>delete all existing clients</strong> and replace them with the uploaded Excel file. This action cannot be undone.</p>
          </div>
          <label className="inline-flex items-center gap-2 cursor-pointer">
            <input type="file" accept=".xlsx,.xls" onChange={handleImportReplace} className="hidden" data-testid="admin-import-file" disabled={importing} />
            <Button variant="outline" className="pointer-events-none" disabled={importing}>
              {importing ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Upload className="w-4 h-4 mr-2" />}
              {importing ? "Importing..." : "Upload & Replace"}
            </Button>
          </label>
          {importResult && (
            <div className={`mt-3 p-3 rounded-lg text-sm ${importResult.success ? "bg-emerald-50 border border-emerald-200 text-emerald-700" : "bg-red-50 border border-red-200 text-red-700"}`} data-testid="import-result">
              {importResult.message}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ── Tab: Revenue Targets ──
function RevenueTargetsTab() {
  const [targets, setTargets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newQuarter, setNewQuarter] = useState("");
  const [newTargetRevenue, setNewTargetRevenue] = useState("");
  const [newCurrency, setNewCurrency] = useState("NGN");
  const [editingQuarter, setEditingQuarter] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [editCurrency, setEditCurrency] = useState("NGN");

  const fetchTargets = useCallback(async () => {
    try {
      const res = await fetch(`${API}/revenue-targets`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setTargets((data.targets || []).sort((a, b) => a.quarter.localeCompare(b.quarter)));
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchTargets(); }, [fetchTargets]);

  const generateQuarterOptions = () => {
    const now = new Date();
    const year = now.getFullYear();
    const options = [];
    for (let y = year - 1; y <= year + 2; y++) {
      for (let q = 1; q <= 4; q++) {
        options.push(`Q${q} ${y}`);
      }
    }
    return options;
  };

  const existingQuarters = new Set(targets.map(t => t.quarter));

  const handleAdd = async () => {
    if (!newQuarter || !newTargetRevenue) {
      toast.error("Select a quarter and enter a target value");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${API}/revenue-targets`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ quarter: newQuarter, target_revenue: parseFloat(newTargetRevenue), currency: newCurrency }),
      });
      if (res.ok) {
        toast.success(`Target set for ${newQuarter}`);
        setNewQuarter("");
        setNewTargetRevenue("");
        fetchTargets();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to set target");
      }
    } catch { toast.error("Failed to set target"); }
    finally { setSaving(false); }
  };

  const handleUpdate = async (quarter) => {
    if (!editValue) { toast.error("Enter a target value"); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API}/revenue-targets`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ quarter, target_revenue: parseFloat(editValue), currency: editCurrency }),
      });
      if (res.ok) {
        toast.success(`Target updated for ${quarter}`);
        setEditingQuarter(null);
        fetchTargets();
      } else { toast.error("Failed to update target"); }
    } catch { toast.error("Failed to update target"); }
    finally { setSaving(false); }
  };

  const fmtCurrency = (val, curr) => {
    if (!val) return "-";
    return new Intl.NumberFormat("en-NG", { style: "currency", currency: curr || "NGN", maximumFractionDigits: 0 }).format(val);
  };

  return (
    <div className="space-y-5" data-testid="revenue-targets-tab">
      <p className="text-sm text-slate-500">Set quarterly revenue targets. These drive the coverage metrics on the Revenue Dashboard.</p>

      {/* Add New Target */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold uppercase tracking-wider text-slate-500">Add Quarterly Target</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap gap-3 items-end">
            <div>
              <Label className="text-xs text-slate-500">Quarter</Label>
              <select
                value={newQuarter}
                onChange={(e) => setNewQuarter(e.target.value)}
                className="h-9 rounded-md border border-slate-200 px-3 text-sm bg-white"
                data-testid="target-quarter-select"
              >
                <option value="">Select quarter</option>
                {generateQuarterOptions().map(q => (
                  <option key={q} value={q} disabled={existingQuarters.has(q)}>{q}{existingQuarters.has(q) ? " (set)" : ""}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-xs text-slate-500">Target Revenue</Label>
              <Input
                type="number"
                value={newTargetRevenue}
                onChange={(e) => setNewTargetRevenue(e.target.value)}
                placeholder="e.g. 50000000"
                className="w-44"
                data-testid="target-revenue-input"
              />
            </div>
            <div>
              <Label className="text-xs text-slate-500">Currency</Label>
              <select
                value={newCurrency}
                onChange={(e) => setNewCurrency(e.target.value)}
                className="h-9 rounded-md border border-slate-200 px-3 text-sm bg-white"
                data-testid="target-currency-select"
              >
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <Button
              onClick={handleAdd}
              disabled={saving || !newQuarter || !newTargetRevenue}
              className="bg-blue-600 hover:bg-blue-700"
              data-testid="add-target-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1" /> Add Target</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Existing Targets */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold uppercase tracking-wider text-slate-500">Current Targets</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>
          ) : targets.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-8">No quarterly targets set yet. Add your first target above.</p>
          ) : (
            <div className="space-y-2">
              {targets.map((t) => (
                <div key={t.quarter} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100" data-testid={`target-row-${t.quarter}`}>
                  {editingQuarter === t.quarter ? (
                    <div className="flex items-center gap-3 flex-1">
                      <span className="font-medium text-sm text-slate-700 w-20">{t.quarter}</span>
                      <Input
                        type="number"
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        className="w-40"
                        data-testid={`edit-target-value-${t.quarter}`}
                      />
                      <select
                        value={editCurrency}
                        onChange={(e) => setEditCurrency(e.target.value)}
                        className="h-9 rounded-md border border-slate-200 px-2 text-sm bg-white"
                      >
                        {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                      <Button size="sm" onClick={() => handleUpdate(t.quarter)} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700">
                        <Check className="w-3 h-3 mr-1" /> Save
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingQuarter(null)}>
                        <X className="w-3 h-3" />
                      </Button>
                    </div>
                  ) : (
                    <>
                      <div className="flex items-center gap-4">
                        <span className="font-semibold text-sm text-slate-800 w-20">{t.quarter}</span>
                        <span className="text-sm text-slate-600">{fmtCurrency(t.target_revenue, t.currency)}</span>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => { setEditingQuarter(t.quarter); setEditValue(t.target_revenue); setEditCurrency(t.currency || "NGN"); }}
                        className="text-slate-500 hover:text-blue-600"
                        data-testid={`edit-target-btn-${t.quarter}`}
                      >
                        <Pencil className="w-3 h-3 mr-1" /> Edit
                      </Button>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ═══════════════════════════════════════════
// MAIN ADMIN CONSOLE PAGE
// ═══════════════════════════════════════════

const BYPASS_REASONS = [
  { key: "habit", label: "Habit", color: "bg-slate-100 text-slate-700" },
  { key: "urgency", label: "Urgency", color: "bg-red-50 text-red-700" },
  { key: "low_trust", label: "Low Trust", color: "bg-amber-50 text-amber-700" },
  { key: "missing_standard", label: "Missing Standard", color: "bg-blue-50 text-blue-700" },
  { key: "unclear_question", label: "Unclear Question", color: "bg-violet-50 text-violet-700" },
  { key: "founder_convenience", label: "Founder Convenience", color: "bg-orange-50 text-orange-700" },
  { key: "system_latency", label: "System Latency", color: "bg-cyan-50 text-cyan-700" },
  { key: "wrong_retrieval", label: "Wrong Retrieval", color: "bg-pink-50 text-pink-700" },
];

// ── Server-side cache admin card (May 2026) ──
// Surfaces the in-process TTL cache that speeds up Postgres-heavy endpoints
// (Revenue, workspaces, FX, membership). Lets admins manually reload when
// numbers look stale — e.g. right after a bulk data import — without
// restarting the backend.
function CacheAdminCard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin-console/cache/stats`, { credentials: "include" });
      if (r.ok) setStats(await r.json());
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  const invalidate = async (prefix, label) => {
    setWorking(prefix || "all");
    try {
      const r = await fetch(`${API}/admin-console/cache/invalidate`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ prefix: prefix || null }),
      });
      if (r.ok) {
        const d = await r.json();
        toast.success(`${label} cleared — ${d.cleared} entr${d.cleared === 1 ? "y" : "ies"} flushed`);
        fetchStats();
      } else {
        toast.error("Failed to clear cache");
      }
    } finally { setWorking(null); }
  };

  return (
    <Card data-testid="cache-admin-card" className="border-indigo-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <RefreshCw className="h-4 w-4 text-indigo-600" /> Server cache
          <Badge variant="outline" className="text-[9px] text-slate-400 ml-1">
            {stats?.backend || "in-process"}
          </Badge>
          <button
            onClick={fetchStats}
            className="ml-auto text-[10px] text-slate-400 hover:text-slate-600"
            data-testid="cache-refresh-stats"
          >
            Refresh stats
          </button>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-xs text-slate-500">
          Speeds up Revenue, Amplify and Brand Manager by caching Neon round-trips in memory. TTLs: Revenue 45s · Workspaces 60s · Membership 120s · FX 300s. If numbers look stale after a bulk import or an FX edit, clear the relevant bucket here.
        </p>
        {loading ? (
          <p className="text-xs text-slate-400">Loading cache stats…</p>
        ) : stats ? (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="cache-stats-grid">
              {Object.entries(stats.known_prefixes).map(([prefix, label]) => {
                const count = stats.by_prefix?.[prefix] || 0;
                return (
                  <div key={prefix} className="rounded-md border border-slate-200 p-2" data-testid={`cache-bucket-${prefix}`}>
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] font-medium text-slate-700">{label}</span>
                      <Badge variant="outline" className="text-[10px]">{count}</Badge>
                    </div>
                    <button
                      onClick={() => invalidate(prefix, label)}
                      disabled={count === 0 || working === prefix}
                      className="mt-1.5 w-full text-[10px] text-indigo-600 hover:text-indigo-800 disabled:opacity-40 disabled:cursor-not-allowed"
                      data-testid={`cache-clear-${prefix}`}
                    >
                      {working === prefix ? "Clearing…" : "Clear"}
                    </button>
                  </div>
                );
              })}
            </div>
            <div className="flex items-center justify-between text-[11px] text-slate-500 pt-1 border-t border-slate-100">
              <span data-testid="cache-total-entries">
                {stats.total_entries} active entr{stats.total_entries === 1 ? "y" : "ies"}
                {stats.oldest_entry_age_s != null && ` · oldest ${stats.oldest_entry_age_s}s`}
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => invalidate("", "All caches")}
                disabled={stats.total_entries === 0 || working === "all"}
                className="h-7 border-red-300 text-red-700 hover:bg-red-50"
                data-testid="cache-clear-all"
              >
                {working === "all" ? "Clearing…" : "Clear everything"}
              </Button>
            </div>
          </>
        ) : (
          <p className="text-xs text-slate-400">Cache stats unavailable.</p>
        )}
      </CardContent>
    </Card>
  );
}


function RexOperationsTab() {
  const [metrics, setMetrics] = useState(null);
  const [topStandards, setTopStandards] = useState(null);
  const [rollout, setRollout] = useState(null);
  const [metricsExpanded, setMetricsExpanded] = useState(true);
  const [rolloutExpanded, setRolloutExpanded] = useState(true);
  const [actionInProgress, setActionInProgress] = useState(null);
  const [logging, setLogging] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [mRes, sRes, rRes] = await Promise.all([
        fetch(`${API}/operating-metrics?days=30`, { credentials: "include" }),
        fetch(`${API}/operating-metrics/top-standards?days=30&limit=5`, { credentials: "include" }),
        fetch(`${API}/rollout/experiment-summary`, { credentials: "include" }),
      ]);
      if (mRes.ok) setMetrics(await mRes.json());
      if (sRes.ok) setTopStandards(await sRes.json());
      if (rRes.ok) setRollout(await rRes.json());
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const performRolloutAction = async (action) => {
    setActionInProgress(action);
    try {
      const res = await fetch(`${API}/rollout/${action}`, { method: "POST", credentials: "include" });
      if (res.ok) {
        toast.success(`Rollout ${action.replace("-", " ")} successful`);
        fetchAll();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || err.error || `Failed to ${action}`);
      }
    } catch { toast.error(`Failed to ${action}`); }
    finally { setActionInProgress(null); }
  };

  const logBypass = async (reason) => {
    setLogging(true);
    try {
      const res = await fetch(`${API}/rollout/bypass`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        credentials: "include", body: JSON.stringify({ reason, source: "ui_button" }),
      });
      if (res.ok) {
        const data = await res.json();
        toast.success(`Bypass logged: ${reason.replace("_", " ")}`, { description: data.recovery_suggestion, duration: 6000 });
        fetchAll();
      }
    } catch { toast.error("Failed to log bypass"); }
    finally { setLogging(false); }
  };

  const trendIcon = (trend) => trend === "up" ? <TrendingUp className="w-3 h-3 text-emerald-500" /> : trend === "down" ? <TrendingDown className="w-3 h-3 text-blue-500" /> : null;

  const phase = rollout?.phase || "not_started";
  const phaseConfig = {
    not_started: { label: "Not Started", color: "bg-slate-100 text-slate-600", icon: Play },
    shadow: { label: "Shadow Mode (Baseline)", color: "bg-amber-100 text-amber-700", icon: ShieldCheck },
    active: { label: "Active Rollout", color: "bg-emerald-100 text-emerald-700", icon: Zap },
    graduated: { label: "Graduated", color: "bg-blue-100 text-blue-700", icon: Award },
  };
  const pc = phaseConfig[phase] || phaseConfig.not_started;
  const PhaseIcon = pc.icon;

  return (
    <div className="space-y-6" data-testid="rex-operations-tab">
      {/* ── Server Cache Admin (May 2026) ── */}
      <CacheAdminCard />

      {/* ── Rex Operating Metrics ── */}
      <Card data-testid="rex-operating-metrics">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              <BarChart3 className="h-4 w-4 text-slate-600" /> Rex Operating Metrics
              <Badge variant="outline" className="text-[9px] text-slate-400">30d</Badge>
            </CardTitle>
            <button onClick={() => setMetricsExpanded(!metricsExpanded)}
              className="text-xs text-slate-400 hover:text-slate-600" data-testid="metrics-expand-toggle">
              {metricsExpanded ? "Collapse" : "Expand"}
            </button>
          </div>
        </CardHeader>
        {metrics && (
          <CardContent>
            <div className="grid grid-cols-4 gap-3">
              <div className="bg-slate-50 rounded-lg p-3 text-center" data-testid="kpi-displacement-rate">
                <p className="text-lg font-bold text-slate-800">{metrics.displacement_rate || 0}%</p>
                <p className="text-[10px] text-slate-500 mt-1">Displacement Rate</p>
                <p className="text-[9px] text-slate-400">{metrics.avoided_interruptions} avoided / {metrics.founder_takeovers || 0} takeovers</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 text-center" data-testid="kpi-avoided-interruptions">
                <div className="flex items-center justify-center gap-1">
                  <p className="text-lg font-bold text-slate-800">{metrics.avoided_interruptions}</p>
                  {trendIcon(metrics.avoided_trend)}
                </div>
                <p className="text-[10px] text-slate-500 mt-1">Interruptions Avoided</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 text-center" data-testid="kpi-corrections-week">
                <div className="flex items-center justify-center gap-1">
                  <p className="text-lg font-bold text-slate-800">{metrics.corrections_per_week}</p>
                  {trendIcon(metrics.corrections_trend)}
                </div>
                <p className="text-[10px] text-slate-500 mt-1">Corrections / Week</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 text-center" data-testid="kpi-standards-active">
                <p className="text-lg font-bold text-slate-800">{metrics.active_standards}</p>
                <p className="text-[10px] text-slate-500 mt-1">Standards Active</p>
              </div>
            </div>

            {metricsExpanded && (
              <div className="mt-4 pt-3 border-t border-slate-100 space-y-3">
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-slate-50 rounded-lg p-2.5" data-testid="kpi-proposal-depth">
                    <p className="text-[10px] font-semibold text-slate-500 mb-1">Proposal Quality</p>
                    <p className="text-xs text-slate-700">
                      {metrics.proposal_edit_depth?.total_proposals || 0} proposals
                      {metrics.proposal_edit_depth?.autonomy_rate > 0 && ` | ${metrics.proposal_edit_depth.autonomy_rate}% autonomous`}
                    </p>
                    {metrics.proposal_edit_depth?.total_proposals > 0 && (
                      <div className="mt-1 space-y-0.5 text-[10px] text-slate-500">
                        {metrics.proposal_edit_depth.accepted_first_draft > 0 && <p>First draft: {metrics.proposal_edit_depth.accepted_first_draft}</p>}
                        {metrics.proposal_edit_depth.minor_polish > 0 && <p>Minor polish: {metrics.proposal_edit_depth.minor_polish}</p>}
                        {metrics.proposal_edit_depth.medium_refinement > 0 && <p>Medium refinement: {metrics.proposal_edit_depth.medium_refinement}</p>}
                        {metrics.proposal_edit_depth.structural_rewrite > 0 && <p>Structural rewrite: {metrics.proposal_edit_depth.structural_rewrite}</p>}
                        {metrics.proposal_edit_depth.founder_takeover > 0 && <p>Founder takeover: {metrics.proposal_edit_depth.founder_takeover}</p>}
                      </div>
                    )}
                  </div>
                  <div className="bg-slate-50 rounded-lg p-2.5" data-testid="kpi-checklist-miss">
                    <p className="text-[10px] font-semibold text-slate-500 mb-1">Checklist Miss Rate</p>
                    <p className="text-xs text-slate-700">
                      {metrics.checklist_miss_rate?.miss_rate || 0}%
                      <span className="text-slate-400 ml-1">target: 0%</span>
                    </p>
                    {metrics.checklist_miss_rate?.miss_despite_standard > 0 && (
                      <p className="text-[10px] text-red-500 mt-0.5">{metrics.checklist_miss_rate.miss_despite_standard} despite existing standard</p>
                    )}
                    {metrics.checklist_miss_rate?.by_checklist_family && Object.keys(metrics.checklist_miss_rate.by_checklist_family).length > 0 && (
                      <div className="mt-1 space-y-0.5 text-[10px] text-slate-500">
                        {Object.entries(metrics.checklist_miss_rate.by_checklist_family).map(([family, count]) => (
                          <p key={family}>{family}: {count} misses</p>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="bg-slate-50 rounded-lg p-2.5" data-testid="kpi-reuse-success">
                    <p className="text-[10px] font-semibold text-slate-500 mb-1">Reuse Success</p>
                    <p className="text-xs text-slate-700">{metrics.reuse_success_rate}% of applied standards accepted</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">{metrics.team_queries} team queries</p>
                  </div>
                </div>

                {topStandards?.top_standards?.length > 0 && (
                  <div className="bg-slate-50 rounded-lg p-2.5" data-testid="kpi-top-standards">
                    <p className="text-[10px] font-semibold text-slate-500 mb-2">Top Standards by Business Impact</p>
                    <div className="space-y-1.5">
                      {topStandards.top_standards.map((std, i) => (
                        <div key={std.standard_id || i} className="flex items-center justify-between text-[10px]" data-testid={`top-standard-${i}`}>
                          <div className="flex items-center gap-1.5 min-w-0 flex-1">
                            <span className="text-slate-400 font-bold w-3">{i + 1}</span>
                            <Badge variant="outline" className="text-[8px] px-1 py-0 shrink-0">{std.domain_label}</Badge>
                            <span className="text-slate-700 truncate">{std.delta_rule?.slice(0, 50)}{std.delta_rule?.length > 50 ? "..." : ""}</span>
                          </div>
                          <div className="flex items-center gap-2 shrink-0 ml-2">
                            <span className="text-slate-400">{std.last_applied_count}x</span>
                            <span className="font-semibold text-slate-700">{std.business_impact_score}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-[9px] text-slate-400 mt-1">Ranked by usage x domain weight x recency</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        )}
        {!metrics && <CardContent><p className="text-xs text-slate-400">Loading metrics...</p></CardContent>}
      </Card>

      {/* ── Rollout Experiment ── */}
      <Card data-testid="rollout-experiment-card">
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <ShieldCheck className="h-4 w-4 text-slate-600" /> Rollout Experiment
              </CardTitle>
              <Badge className={`text-[9px] ${pc.color}`} variant="outline">
                <PhaseIcon className="w-2.5 h-2.5 mr-0.5" /> {pc.label}
              </Badge>
              {rollout?.days_elapsed > 0 && <span className="text-[10px] text-slate-400">Day {rollout.days_elapsed}</span>}
            </div>
            <div className="flex items-center gap-1.5">
              {phase === "not_started" && (
                <Button size="sm" className="h-7 text-xs px-3" data-testid="start-baseline-btn"
                  onClick={() => performRolloutAction("start-baseline")} disabled={!!actionInProgress}>
                  {actionInProgress === "start-baseline" ? <Loader2 className="w-3 h-3 animate-spin" /> : "Start Baseline"}
                </Button>
              )}
              {phase === "shadow" && (
                <Button size="sm" className="h-7 text-xs px-3 bg-emerald-600 hover:bg-emerald-700" data-testid="activate-rollout-btn"
                  onClick={() => performRolloutAction("activate")} disabled={!!actionInProgress}>
                  {actionInProgress === "activate" ? <Loader2 className="w-3 h-3 animate-spin" /> : "Activate Rollout"}
                </Button>
              )}
              {phase === "active" && (
                <Button size="sm" variant="outline" className="h-7 text-xs px-3" data-testid="graduate-rollout-btn"
                  onClick={() => performRolloutAction("graduate")} disabled={!!actionInProgress}>
                  Graduate
                </Button>
              )}
              <button onClick={() => setRolloutExpanded(!rolloutExpanded)}
                className="text-xs text-slate-400 hover:text-slate-600" data-testid="rollout-expand-toggle">
                {rolloutExpanded ? "Collapse" : "Expand"}
              </button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-3">
            <div className="bg-slate-50 rounded-lg p-3 text-center" data-testid="rollout-bypass-count">
              <p className="text-lg font-bold text-slate-800">{rollout?.bypass_count || 0}</p>
              <p className="text-[10px] text-slate-500 mt-1">Bypasses Logged</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-center" data-testid="rollout-stories-count">
              <p className="text-lg font-bold text-slate-800">{rollout?.leverage_stories_count || 0}<span className="text-slate-400 text-sm">/{rollout?.leverage_stories_target || 10}</span></p>
              <p className="text-[10px] text-slate-500 mt-1">Leverage Stories</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-center" data-testid="rollout-comparison-displacement">
              <p className="text-lg font-bold text-slate-800">
                {rollout?.comparison?.displacement_rate?.current ?? "—"}
                {rollout?.comparison?.displacement_rate?.current !== undefined && "%"}
              </p>
              <p className="text-[10px] text-slate-500 mt-1">Displacement Rate</p>
            </div>
            <div className="bg-slate-50 rounded-lg p-3 text-center" data-testid="rollout-days-elapsed">
              <p className="text-lg font-bold text-slate-800">{rollout?.days_elapsed || 0}</p>
              <p className="text-[10px] text-slate-500 mt-1">Days Elapsed</p>
            </div>
          </div>

          {(phase === "shadow" || phase === "active") && (
            <div className="mt-4 pt-3 border-t border-slate-100" data-testid="quick-bypass-section">
              <p className="text-[10px] font-semibold text-slate-500 mb-2">Quick Bypass Log</p>
              <div className="flex flex-wrap gap-1.5">
                {BYPASS_REASONS.map((r) => (
                  <button key={r.key} onClick={() => logBypass(r.key)} disabled={logging}
                    className={`px-2.5 py-1 rounded-full text-[10px] font-medium transition-all hover:ring-1 hover:ring-slate-300 ${r.color} ${logging ? "opacity-50" : "cursor-pointer"}`}
                    data-testid={`bypass-chip-${r.key}`}>
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {rolloutExpanded && (
            <div className="mt-4 pt-3 border-t border-slate-100 space-y-3">
              {rollout?.bypass_taxonomy && Object.keys(rollout.bypass_taxonomy).length > 0 && (
                <div className="bg-slate-50 rounded-lg p-2.5" data-testid="bypass-taxonomy-section">
                  <p className="text-[10px] font-semibold text-slate-500 mb-2">Bypass Taxonomy</p>
                  <div className="space-y-1">
                    {Object.entries(rollout.bypass_taxonomy).sort((a, b) => b[1] - a[1]).map(([reason, count]) => (
                      <div key={reason} className="flex items-center justify-between text-[10px]">
                        <span className="text-slate-600">{reason.replace(/_/g, " ")}</span>
                        <span className="font-semibold text-slate-800">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {rollout?.comparison && (
                <div className="bg-slate-50 rounded-lg p-2.5" data-testid="baseline-comparison-section">
                  <p className="text-[10px] font-semibold text-slate-500 mb-2">Baseline vs Current</p>
                  <div className="grid grid-cols-2 gap-2">
                    {Object.entries(rollout.comparison).map(([key, val]) => (
                      <div key={key} className="flex items-center justify-between text-[10px]">
                        <span className="text-slate-600">{val.label || key}</span>
                        <span className="font-semibold">
                          {val.baseline !== undefined ? (
                            <span>
                              <span className="text-slate-400">{val.baseline}</span>
                              <span className="mx-1">&rarr;</span>
                              <span className={val.change > 0 ? "text-emerald-600" : val.change < 0 ? "text-red-500" : "text-slate-700"}>{val.current}</span>
                            </span>
                          ) : (<span className="text-slate-700">{val.current ?? "—"}</span>)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


// ── Tab: Pipeline Settings (Feb 2026) ──
// Configurable stage probabilities used by the weighted-pipeline calc on
// the Today widget + Revenue dashboard. Stored in mongo `pipeline_stage_config`.
function PipelineSettingsTab() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [calibrating, setCalibrating] = useState(false);
  const [calibPreview, setCalibPreview] = useState(null);
  const [dirty, setDirty] = useState({});

  const fetchCfg = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/opportunities/pipeline-stages/config`, { credentials: "include" });
      if (r.ok) setCfg(await r.json());
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { fetchCfg(); }, [fetchCfg]);

  const stageList = cfg ? Object.keys(cfg.defaults) : [];

  const handleChange = (stage, val) => setDirty({ ...dirty, [stage]: val });

  const handleSave = async () => {
    if (!cfg) return;
    setSaving(true);
    try {
      const merged = { ...cfg.stages, ...dirty };
      const clean = {};
      for (const s of stageList) {
        const v = parseFloat(merged[s]);
        if (!isNaN(v) && v >= 0 && v <= 1) clean[s] = v;
      }
      const r = await fetch(`${API}/opportunities/pipeline-stages/config`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ stages: clean }),
      });
      if (r.ok) {
        toast.success("Stage probabilities saved");
        setDirty({});
        fetchCfg();
      } else {
        toast.error("Failed to save");
      }
    } finally { setSaving(false); }
  };

  const resetAll = () => {
    if (!cfg) return;
    const reset = {};
    for (const s of stageList) reset[s] = cfg.defaults[s];
    setDirty(reset);
  };

  const previewCalibration = async () => {
    setCalibrating(true);
    try {
      const r = await fetch(`${API}/opportunities/pipeline-stages/auto-calibrate?months=12`, { credentials: "include" });
      if (r.ok) {
        setCalibPreview(await r.json());
      } else {
        toast.error("Failed to preview calibration");
      }
    } finally { setCalibrating(false); }
  };

  const applyCalibration = async () => {
    setCalibrating(true);
    try {
      const r = await fetch(`${API}/opportunities/pipeline-stages/auto-calibrate?months=12`, {
        method: "POST", credentials: "include",
      });
      if (r.ok) {
        const data = await r.json();
        toast.success(`Auto-calibrated: ${data.applied.length} stage(s) updated, ${data.skipped_insufficient_data.length} kept (insufficient data).`);
        setCalibPreview(null);
        setDirty({});
        fetchCfg();
      } else {
        toast.error("Failed to apply calibration");
      }
    } finally { setCalibrating(false); }
  };

  if (loading || !cfg) {
    return <div className="flex items-center gap-2 text-slate-500 p-6"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>;
  }
  return (
    <div className="space-y-4" data-testid="pipeline-settings-tab">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Pipeline stage probabilities</h2>
        <p className="text-xs text-slate-500 mt-1">Default win probabilities applied to deals in each stage. These drive the weighted pipeline number on the Today page and Revenue dashboard. Dynamic learning from historical data overrides these when 5+ deals have visited a stage.</p>
      </div>
      <Card className="border-slate-200">
        <CardContent className="p-4 space-y-3">
          {stageList.map((s) => {
            const current = dirty[s] != null ? dirty[s] : cfg.stages[s];
            const currentVal = typeof current === "number" ? current : parseFloat(current);
            return (
              <div key={s} className="flex items-center gap-3" data-testid={`pipeline-stage-${s.replace(/\s+/g, '-').toLowerCase()}`}>
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-800">{s}</p>
                  <p className="text-[11px] text-slate-400">Default {Math.round((cfg.defaults[s] || 0) * 100)}%</p>
                </div>
                <Input
                  type="number"
                  min="0"
                  max="1"
                  step="0.05"
                  value={current}
                  onChange={(e) => handleChange(s, e.target.value)}
                  className="w-28"
                  data-testid={`pipeline-stage-input-${s.replace(/\s+/g, '-').toLowerCase()}`}
                />
                <span className="text-xs text-slate-500 w-12 text-right tabular-nums">{isNaN(currentVal) ? "—" : `${Math.round(currentVal * 100)}%`}</span>
              </div>
            );
          })}
          <div className="flex items-center gap-2 pt-2 border-t border-slate-100">
            <Button onClick={handleSave} disabled={saving || !Object.keys(dirty).length} data-testid="pipeline-settings-save">
              {saving ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Save className="h-3 w-3 mr-1" />} Save
            </Button>
            <Button variant="outline" onClick={resetAll} data-testid="pipeline-settings-reset">Reset to defaults</Button>
            <Button
              variant="outline"
              onClick={previewCalibration}
              disabled={calibrating}
              className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
              data-testid="pipeline-settings-auto-calibrate"
            >
              {calibrating ? <Loader2 className="h-3 w-3 mr-1 animate-spin"/> : null}
              Auto-calibrate from 12-month history
            </Button>
            {cfg.updated_at && (
              <span className="text-[11px] text-slate-400 ml-auto">Last updated {new Date(cfg.updated_at).toLocaleString()} {cfg.updated_by ? `by ${cfg.updated_by}` : ""}</span>
            )}
          </div>
          {calibPreview && (
            <div className="mt-3 rounded-md border border-indigo-200 bg-indigo-50/40 p-3" data-testid="pipeline-calib-preview">
              <p className="text-sm font-semibold text-indigo-900 mb-1">Calibration preview · {calibPreview.window_months}-month window · {calibPreview.total_opportunities} opportunities</p>
              <p className="text-[11px] text-slate-600 mb-2">Stages with fewer than 5 historical data points keep their existing value.</p>
              <div className="space-y-1">
                {Object.entries(calibPreview.stages).map(([stage, info]) => (
                  <div key={stage} className="flex items-center gap-2 text-xs" data-testid={`calib-row-${stage.replace(/\s+/g, '-').toLowerCase()}`}>
                    <span className="flex-1 text-slate-800">{stage}</span>
                    <span className="text-slate-500 tabular-nums w-20 text-right">{info.reached} reached / {info.won} won</span>
                    <span className={`tabular-nums w-16 text-right font-medium ${info.sample_sufficient ? 'text-emerald-700' : 'text-slate-400'}`}>
                      {info.rate != null ? `${Math.round(info.rate * 100)}%` : "—"}
                    </span>
                    {!info.sample_sufficient && <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-700">low data</Badge>}
                  </div>
                ))}
              </div>
              <div className="flex gap-2 mt-3">
                <Button
                  size="sm"
                  onClick={applyCalibration}
                  disabled={calibrating}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white"
                  data-testid="pipeline-calib-apply"
                >
                  {calibrating ? <Loader2 className="h-3 w-3 mr-1 animate-spin"/> : null}
                  Apply calibration
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setCalibPreview(null)}
                  data-testid="pipeline-calib-cancel"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}


// ── Tab: FX Rates Admin (Feb 2026) ──
// Role-gated, with a "Refresh from API" button that pulls latest rates
// from open.er-api.com (free, no key). Keeps manual overrides when present.
function FxRatesAdminTab() {
  const [wsId, setWsId] = useState(null);
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [newRow, setNewRow] = useState({ from: "", to: "", rate: "" });

  useEffect(() => {
    fetchMyWorkspaces()
      .then((rows) => rows?.[0] && setWsId(rows[0].workspace_id))
      .catch(() => {});
  }, []);

  const fetchRates = useCallback(async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const r = await fetch(`${API}/v2/workspaces/${wsId}/fx-rates`, { credentials: "include" });
      if (r.ok) setRates(await r.json());
    } finally { setLoading(false); }
  }, [wsId]);
  useEffect(() => { fetchRates(); }, [fetchRates]);

  const handleRefresh = async () => {
    if (!wsId) return;
    setRefreshing(true);
    try {
      const r = await fetch(`${API}/v2/workspaces/${wsId}/fx-rates/refresh`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ preserve_manual: true }),
      });
      if (r.ok) {
        const d = await r.json();
        toast.success(`Refreshed ${d.refreshed_count} rates (${d.skipped_count} skipped)`);
        fetchRates();
      } else {
        const err = await r.json().catch(() => ({}));
        toast.error(err.detail || "Refresh failed");
      }
    } catch (e) {
      toast.error("Network error");
    } finally { setRefreshing(false); }
  };

  const handleUpsert = async () => {
    if (!wsId || !newRow.from || !newRow.to || !newRow.rate) return;
    const r = await fetch(`${API}/v2/workspaces/${wsId}/fx-rates/${newRow.from.toUpperCase()}/${newRow.to.toUpperCase()}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        from_currency: newRow.from.toUpperCase(),
        to_currency: newRow.to.toUpperCase(),
        rate: parseFloat(newRow.rate),
      }),
    });
    if (r.ok) {
      toast.success("Rate saved");
      setNewRow({ from: "", to: "", rate: "" });
      fetchRates();
    } else { toast.error("Save failed"); }
  };

  const handleDelete = async (fc, tc) => {
    if (!wsId) return;
    if (!window.confirm(`Delete ${fc} → ${tc}?`)) return;
    const r = await fetch(`${API}/v2/workspaces/${wsId}/fx-rates/${fc}/${tc}`, {
      method: "DELETE",
      credentials: "include",
    });
    if (r.ok) fetchRates();
  };

  return (
    <div className="space-y-4" data-testid="fx-rates-admin-tab">
      <div className="flex items-baseline justify-between flex-wrap gap-2">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">FX Rates</h2>
          <p className="text-xs text-slate-500 mt-1">Workspace exchange rates used by the Revenue dashboard. Manual rows are preserved when you refresh.</p>
        </div>
        <Button onClick={handleRefresh} disabled={refreshing || !wsId} data-testid="fx-refresh-btn">
          {refreshing ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <RefreshCw className="h-3 w-3 mr-1" />} Refresh from API
        </Button>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-end gap-2 mb-3" data-testid="fx-add-row">
            <div>
              <Label className="text-xs">From</Label>
              <Input placeholder="GBP" className="w-20" value={newRow.from} onChange={(e) => setNewRow({ ...newRow, from: e.target.value })} data-testid="fx-add-from" />
            </div>
            <div>
              <Label className="text-xs">To</Label>
              <Input placeholder="USD" className="w-20" value={newRow.to} onChange={(e) => setNewRow({ ...newRow, to: e.target.value })} data-testid="fx-add-to" />
            </div>
            <div>
              <Label className="text-xs">Rate</Label>
              <Input type="number" placeholder="1.35" className="w-28" value={newRow.rate} onChange={(e) => setNewRow({ ...newRow, rate: e.target.value })} data-testid="fx-add-rate" />
            </div>
            <Button size="sm" onClick={handleUpsert} disabled={!newRow.from || !newRow.to || !newRow.rate} data-testid="fx-add-save"><Plus className="h-3 w-3 mr-1" /> Add / update</Button>
          </div>
          {loading ? (
            <div className="flex items-center gap-2 text-slate-500 py-4"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
          ) : rates.length === 0 ? (
            <p className="text-xs text-slate-400 py-2">No rates yet — click "Refresh from API" to seed.</p>
          ) : (
            <table className="w-full text-sm" data-testid="fx-rates-table">
              <thead><tr className="text-xs uppercase text-slate-500"><th className="text-left">Pair</th><th className="text-right">Rate</th><th className="text-right">Source</th><th className="text-right">Updated</th><th></th></tr></thead>
              <tbody>
                {rates.map((r) => (
                  <tr key={r.fx_id} className="border-t border-slate-100" data-testid={`fx-row-${r.from_currency}-${r.to_currency}`}>
                    <td className="py-1 font-medium">{r.from_currency} → {r.to_currency}</td>
                    <td className="text-right tabular-nums">{r.rate.toFixed(6)}</td>
                    <td className="text-right">
                      <Badge variant="outline" className={`text-[10px] ${r.source === "api" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-slate-50 text-slate-600 border-slate-200"}`}>
                        {r.source || "manual"}
                      </Badge>
                    </td>
                    <td className="text-right text-xs text-slate-500">{r.updated_at ? new Date(r.updated_at).toLocaleDateString() : "—"}</td>
                    <td className="text-right">
                      <button className="text-red-500 hover:text-red-700 text-xs" onClick={() => handleDelete(r.from_currency, r.to_currency)} data-testid={`fx-delete-${r.from_currency}-${r.to_currency}`}>
                        <Trash2 className="h-3 w-3 inline" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}



// ── Tab: AI Models (Intelligence Round, Item 1) ──
function AiModelsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [probeResult, setProbeResult] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/admin/model-registry`, { credentials: "include" });
      if (r.ok) setData(await r.json());
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const post = async (path, body, key) => {
    setBusy(key);
    try {
      const r = await fetch(`${API}/admin/model-registry/${path}`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
      });
      if (path === "probe") {
        const j = await r.json();
        setProbeResult((p) => ({ ...p, [body.model_key]: j.reachable ? "reachable" : "unreachable" }));
      } else { await load(); }
    } finally { setBusy(""); }
  };

  if (loading) return <div className="text-sm text-slate-500" data-testid="ai-models-loading">Loading models…</div>;
  if (!data) return <div className="text-sm text-red-600">Could not load the model registry.</div>;

  const { catalog, config, resolved, purposes } = data;
  const purposeLabels = { agent: "Agent reasoning", background: "Background jobs", lightweight: "Lightweight lookups" };

  return (
    <div className="space-y-6" data-testid="ai-models-tab">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">AI Models</h2>
        <p className="text-xs text-slate-500 mt-1">Choose which model Rex runs on. Changes take effect immediately, no redeploy. All models run through the Emergent key.</p>
      </div>

      <div className="rounded-lg border border-slate-200 p-4">
        <div className="text-sm font-medium text-slate-800 mb-3">Default model</div>
        <select
          data-testid="ai-model-default-select"
          className="w-full max-w-md border border-slate-300 rounded-md px-3 py-2 text-sm"
          value={config.default_model_key || ""}
          onChange={(e) => post("default", { model_key: e.target.value }, "default")}
          disabled={busy === "default"}
        >
          {catalog.map((m) => <option key={m.model_key} value={m.model_key}>{m.label}</option>)}
        </select>
      </div>

      <div className="rounded-lg border border-slate-200 p-4">
        <div className="text-sm font-medium text-slate-800 mb-3">Per-purpose overrides</div>
        <div className="space-y-3">
          {purposes.map((p) => (
            <div key={p} className="flex flex-col sm:flex-row sm:items-center gap-2">
              <div className="w-44 text-sm text-slate-600">{purposeLabels[p] || p}</div>
              <select
                data-testid={`ai-model-purpose-${p}`}
                className="flex-1 border border-slate-300 rounded-md px-3 py-2 text-sm"
                value={(config.purpose_overrides || {})[p] || config.default_model_key || ""}
                onChange={(e) => post("purpose", { purpose: p, model_key: e.target.value }, `purpose-${p}`)}
                disabled={busy === `purpose-${p}`}
              >
                {catalog.map((m) => <option key={m.model_key} value={m.model_key}>{m.label}</option>)}
              </select>
              <div className="text-xs text-slate-400 w-40">now: {resolved[p]?.label}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-lg border border-slate-200 p-4">
        <div className="text-sm font-medium text-slate-800 mb-3">Catalogue ({catalog.length})</div>
        <div className="space-y-2">
          {catalog.map((m) => (
            <div key={m.model_key} data-testid={`ai-model-row-${m.model_key}`} className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 last:border-0">
              <div className="min-w-0">
                <div className="text-sm font-medium text-slate-800">{m.label}</div>
                <div className="text-xs text-slate-500 truncate">{m.provider} · {m.litellm_model} · {m.note}</div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <span className={`text-xs px-2 py-0.5 rounded ${probeResult[m.model_key] === "reachable" || (m.reachable && !probeResult[m.model_key]) ? "bg-green-100 text-green-700" : probeResult[m.model_key] === "unreachable" ? "bg-red-100 text-red-700" : "bg-slate-100 text-slate-500"}`}>
                  {probeResult[m.model_key] || (m.reachable ? "reachable" : "unchecked")}
                </span>
                <button
                  data-testid={`ai-model-probe-${m.model_key}`}
                  onClick={() => post("probe", { model_key: m.model_key }, `probe-${m.model_key}`)}
                  disabled={busy === `probe-${m.model_key}`}
                  className="text-xs px-2 py-1 rounded border border-slate-300 text-slate-600 hover:bg-slate-50"
                >{busy === `probe-${m.model_key}` ? "…" : "Test"}</button>
              </div>
            </div>
          ))}
        </div>
        <p className="text-xs text-slate-400 mt-3">To add a newly released model, POST provider + model to /admin/model-registry/catalog (a config row, no code change).</p>
      </div>
    </div>
  );
}

function MemoryTab() {
  const [data, setData] = useState(null);
  const [recall, setRecall] = useState(null);
  const [learned, setLearned] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [editing, setEditing] = useState(null); // item_id being edited
  const [form, setForm] = useState({ category: "other", title: "", content: "", priority: 100 });
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r1, r2, r3] = await Promise.all([
        fetch(`${API}/admin/standing-knowledge`, { credentials: "include" }),
        fetch(`${API}/admin/memory/recall`, { credentials: "include" }),
        fetch(`${API}/admin/learned-rules`, { credentials: "include" }),
      ]);
      if (r1.ok) setData(await r1.json());
      if (r2.ok) setRecall(await r2.json());
      if (r3.ok) setLearned(await r3.json());
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const failMsg = async (r, fallback) => {
    let detail = fallback;
    try { const b = await r.json(); detail = b?.detail || b?.message || fallback; } catch (e) { /* non-JSON */ }
    return `${detail} (HTTP ${r.status})`;
  };
  const createItem = async () => {
    setBusy("create");
    try {
      const r = await fetch(`${API}/admin/standing-knowledge`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify(form),
      });
      if (r.ok) { setAdding(false); setForm({ category: "other", title: "", content: "", priority: 100 }); await load(); toast.success("Standing knowledge added."); }
      else { toast.error(`Could not add item — ${await failMsg(r, "the change was not saved")}`); }
    } catch (e) { toast.error(`Could not add item — ${e.message || "network error"}. Nothing was saved.`); }
    finally { setBusy(""); }
  };
  const saveItem = async (id, updates) => {
    setBusy(id);
    try {
      const r = await fetch(`${API}/admin/standing-knowledge/${id}`, {
        method: "PUT", credentials: "include",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify(updates),
      });
      if (r.ok) { setEditing(null); await load(); toast.success("Change saved."); }
      else { toast.error(`Change NOT saved — ${await failMsg(r, "Rex still holds the old value")}`); }
    } catch (e) { toast.error(`Change NOT saved — ${e.message || "network error"}. Rex still holds the old value.`); }
    finally { setBusy(""); }
  };
  const deleteItem = async (id) => {
    if (!window.confirm("Delete this belief? Rex will no longer know it.")) return;
    setBusy(id);
    try {
      const r = await fetch(`${API}/admin/standing-knowledge/${id}`, { method: "DELETE", credentials: "include" });
      if (r.ok) { await load(); toast.success("Belief deleted — Rex no longer knows it."); }
      else { toast.error(`NOT deleted — ${await failMsg(r, "Rex still holds this belief")}`); }
    } catch (e) { toast.error(`NOT deleted — ${e.message || "network error"}. Rex still holds this belief.`); }
    finally { setBusy(""); }
  };

  const ruleAction = async (id, verb, method = "POST") => {
    setBusy(id);
    try {
      const r = await fetch(`${API}/admin/learned-rules/${id}${method === "DELETE" ? "" : "/" + verb}`, { method, credentials: "include" });
      if (r.ok) {
        if (method === "DELETE") {
          const body = await r.json();
          const extra = body.derived_deleted ? ` and ${body.derived_deleted} derived rule(s)` : "";
          toast.success(`Rule deleted${extra} — Rex no longer applies it.`);
        } else { toast.success(verb === "approve" ? "Rule approved — Rex will apply it in new sessions." : "Candidate rejected — it will not be proposed again."); }
        await load();
      } else { toast.error(`Action failed — ${await failMsg(r, "the rule was not changed")}`); }
    } catch (e) { toast.error(`Action failed — ${e.message || "network error"}. The rule was not changed.`); }
    finally { setBusy(""); }
  };
  const approveRule = (id) => ruleAction(id, "approve");
  const rejectRule = (id) => ruleAction(id, "reject");
  const deleteRule = (id) => {
    if (!window.confirm("Delete this learned rule (and anything derived solely from it)? Rex reverts to its earlier behaviour.")) return;
    ruleAction(id, "", "DELETE");
  };

  if (loading) return <div className="text-sm text-slate-500" data-testid="memory-loading">Loading Rex's memory…</div>;
  if (!data) return <div className="text-sm text-red-600">Could not load Rex's memory.</div>;

  const overBudget = data.omitted_for_budget > 0;

  return (
    <div className="space-y-6" data-testid="memory-tab">
      <div>
        <h2 className="text-lg font-semibold text-slate-900">Rex Memory</h2>
        <p className="text-xs text-slate-500 mt-1">Everything Rex believes about RESILIGENT, and every document it can recall. Read it, correct it, or delete it. Memory you cannot inspect is memory you cannot trust.</p>
      </div>

      {/* Standing knowledge */}
      <div className="rounded-lg border border-slate-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-medium text-slate-800">Standing knowledge (always in context)</div>
          <button data-testid="memory-add-btn" onClick={() => setAdding((v) => !v)}
            className="text-xs px-3 py-1.5 rounded border border-slate-300 text-slate-700 hover:bg-slate-50">{adding ? "Cancel" : "Add item"}</button>
        </div>
        <div className={`text-xs mb-3 ${overBudget ? "text-red-600" : "text-slate-500"}`} data-testid="memory-budget">
          Using {data.rendered_chars} of {data.budget_chars} characters ({data.included} shown{overBudget ? `, ${data.omitted_for_budget} OMITTED for length — trim items or they will not reach Rex` : ""}).
        </div>

        {adding && (
          <div className="rounded-md border border-slate-200 p-3 mb-3 space-y-2 bg-slate-50" data-testid="memory-add-form">
            <div className="flex gap-2">
              <select data-testid="memory-add-category" className="border border-slate-300 rounded px-2 py-1 text-sm"
                value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {(data.categories || []).map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
              <input data-testid="memory-add-title" placeholder="Title" className="flex-1 border border-slate-300 rounded px-2 py-1 text-sm"
                value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
              <input data-testid="memory-add-priority" type="number" title="priority (lower = more important)" className="w-20 border border-slate-300 rounded px-2 py-1 text-sm"
                value={form.priority} onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value || "100", 10) })} />
            </div>
            <textarea data-testid="memory-add-content" placeholder="What should Rex know?" className="w-full border border-slate-300 rounded px-2 py-1 text-sm" rows={2}
              value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} />
            <button data-testid="memory-add-save" onClick={createItem} disabled={busy === "create" || !form.title || !form.content}
              className="text-xs px-3 py-1.5 rounded bg-slate-900 text-white disabled:opacity-50">{busy === "create" ? "Saving…" : "Save"}</button>
          </div>
        )}

        <div className="space-y-2">
          {data.items.map((it) => (
            <div key={it.item_id} data-testid={`memory-item-${it.item_id}`} className="border-b border-slate-100 last:border-0 py-2">
              {editing === it.item_id ? (
                <div className="space-y-2" data-testid={`memory-edit-${it.item_id}`}>
                  <input className="w-full border border-slate-300 rounded px-2 py-1 text-sm" defaultValue={it.title}
                    onChange={(e) => (it._t = e.target.value)} />
                  <textarea className="w-full border border-slate-300 rounded px-2 py-1 text-sm" rows={2} defaultValue={it.content}
                    onChange={(e) => (it._c = e.target.value)} />
                  <div className="flex gap-2">
                    <button data-testid={`memory-save-${it.item_id}`} onClick={() => saveItem(it.item_id, { title: it._t ?? it.title, content: it._c ?? it.content })}
                      className="text-xs px-3 py-1 rounded bg-slate-900 text-white">Save</button>
                    <button data-testid={`memory-cancel-${it.item_id}`} onClick={() => setEditing(null)} className="text-xs px-3 py-1 rounded border border-slate-300">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{it.category}</span>
                      <span className="text-sm font-medium text-slate-800">{it.title}</span>
                      {!it.active && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">inactive</span>}
                    </div>
                    <div className="text-xs text-slate-600 mt-1">{it.content}</div>
                    <div className="text-[10px] text-slate-400 mt-1" data-testid={`memory-provenance-${it.item_id}`}>
                      source: {it.source} · by {it.updated_by || it.added_by} · {(it.updated_at || it.added_at || "").slice(0, 10)} · priority {it.priority}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button onClick={() => saveItem(it.item_id, { active: !it.active })} disabled={busy === it.item_id}
                      data-testid={`memory-toggle-${it.item_id}`}
                      className="text-xs px-2 py-1 rounded border border-slate-300 text-slate-600 hover:bg-slate-50">{it.active ? "Disable" : "Enable"}</button>
                    <button data-testid={`memory-edit-btn-${it.item_id}`} onClick={() => setEditing(it.item_id)}
                      className="text-xs px-2 py-1 rounded border border-slate-300 text-slate-600 hover:bg-slate-50">Edit</button>
                    <button data-testid={`memory-delete-btn-${it.item_id}`} onClick={() => deleteItem(it.item_id)} disabled={busy === it.item_id}
                      className="text-xs px-2 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50">Delete</button>
                  </div>
                </div>
              )}
            </div>
          ))}
          {data.items.length === 0 && <div className="text-xs text-slate-400">No standing knowledge yet. Add what Rex should always know.</div>}
        </div>
      </div>

      {/* Learned rules (Stage Three) */}
      <div className="rounded-lg border border-slate-200 p-4" data-testid="learned-rules-section">
        <div className="text-sm font-medium text-slate-800 mb-1">Learned rules (from human corrections)</div>
        <p className="text-xs text-slate-500 mb-3">Candidates are proposed from corrections you make. Nothing changes Rex's behaviour until you approve it. Approved rules share the standing-knowledge budget.</p>
        {learned && (
          <div className="text-[11px] text-slate-500 mb-3" data-testid="learned-budget">
            Shared budget {learned.shared_budget_chars} chars: standing uses {learned.standing_uses_chars}, learned rules use {learned.learned_uses_chars}, {learned.budget_remaining_chars} remaining ({learned.learned_included} approved rule(s) in context{learned.learned_omitted_for_budget ? `, ${learned.learned_omitted_for_budget} omitted for length` : ""}). {learned.rejected_count} rejected.
          </div>
        )}

        {/* Candidates awaiting review */}
        <div className="text-xs font-medium text-slate-600 mb-1">Candidates to review ({(learned?.candidates || []).length})</div>
        <div className="space-y-2 mb-4">
          {(learned?.candidates || []).map((r) => (
            <div key={r.rule_id} data-testid={`candidate-${r.rule_id}`} className="rounded-md border border-amber-200 bg-amber-50 p-2.5">
              <div className="text-sm text-slate-800">{r.statement}</div>
              <div className="text-[10px] text-slate-500 mt-1">
                scope: {r.scope} · seen {r.observed_count}x · {(r.evidence?.[0]?.kind || "").replace(/_/g, " ")} · {(r.evidence?.[0]?.what_rex_did || "")} → {(r.evidence?.[0]?.what_human_did || "")}
              </div>
              <div className="flex gap-2 mt-2">
                <button data-testid={`approve-${r.rule_id}`} onClick={() => approveRule(r.rule_id)} disabled={busy === r.rule_id}
                  className="text-xs px-2.5 py-1 rounded bg-emerald-600 text-white disabled:opacity-50">Approve</button>
                <button data-testid={`reject-${r.rule_id}`} onClick={() => rejectRule(r.rule_id)} disabled={busy === r.rule_id}
                  className="text-xs px-2.5 py-1 rounded border border-slate-300 text-slate-600">Reject</button>
              </div>
            </div>
          ))}
          {(learned?.candidates || []).length === 0 && <div className="text-xs text-slate-400">No candidates awaiting review.</div>}
        </div>

        {/* Approved rules in force */}
        <div className="text-xs font-medium text-slate-600 mb-1">Approved rules in force ({(learned?.approved || []).length})</div>
        <div className="space-y-2">
          {(learned?.approved || []).map((r) => (
            <div key={r.rule_id} data-testid={`approved-${r.rule_id}`} className="flex items-start justify-between gap-3 border-b border-slate-100 last:border-0 py-2">
              <div className="min-w-0">
                <div className="text-sm text-slate-800">{r.statement}</div>
                <div className="text-[10px] text-slate-400 mt-1">
                  learned · approved by {r.approved_by || "admin"} · {(r.approved_at || "").slice(0, 10)} · seen {r.observed_count}x
                </div>
              </div>
              <button data-testid={`delete-rule-${r.rule_id}`} onClick={() => deleteRule(r.rule_id)} disabled={busy === r.rule_id}
                className="text-xs px-2 py-1 rounded border border-red-200 text-red-600 hover:bg-red-50 flex-shrink-0">Delete</button>
            </div>
          ))}
          {(learned?.approved || []).length === 0 && <div className="text-xs text-slate-400">No approved learned rules yet.</div>}
        </div>
      </div>

      {/* Recall documents */}
      <div className="rounded-lg border border-slate-200 p-4">
        <div className="text-sm font-medium text-slate-800 mb-1">Recallable documents ({recall?.count || 0})</div>
        <p className="text-xs text-slate-500 mb-3">Documents Rex can find by meaning in later sessions. These are DATA, never instructions.</p>
        <div className="space-y-1.5 max-h-80 overflow-auto" data-testid="memory-recall-list">
          {(recall?.documents || []).map((d) => (
            <div key={d.upload_id} data-testid={`memory-recall-${d.upload_id}`} className="flex items-center justify-between gap-3 text-xs py-1 border-b border-slate-100 last:border-0">
              <div className="min-w-0 truncate text-slate-700">{d.filename || d.upload_id}</div>
              <div className="text-slate-400 flex-shrink-0">{d.source || "doc"} · {d.chunks} chunks · {(d.created_at || "").slice(0, 10)}</div>
            </div>
          ))}
          {(recall?.documents || []).length === 0 && <div className="text-xs text-slate-400">No recallable documents yet.</div>}
        </div>
      </div>
    </div>
  );
}

export default function AdminConsole({ user }) {
  const [activeTab, setActiveTab] = useState("rex-ops");
  const [attachTest, setAttachTest] = useState(null);
  const [runningAttach, setRunningAttach] = useState(false);

  // One-shot, controlled attachment-delivery verification. Sends ONE email with three
  // test files to info@resiligent.com (direct send — does NOT touch the campaign queue
  // and does NOT change the sending pause). In production it is really delivered.
  const runAttachmentTest = async (force = false) => {
    setRunningAttach(true);
    try {
      const res = await fetch(`${API}/campaigns/bespoke/_ops/attachment-delivery-test${force ? "?force=true" : ""}`, {
        method: "POST", credentials: "include",
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || "Attachment test failed"); return; }
      setAttachTest(data);
      if (data.already_sent) toast.info("A real test email was already delivered earlier.");
      else if (data.delivered_externally) toast.success("Test email delivered — check info@resiligent.com");
      else toast.info(`Guard outcome: ${data.guard_outcome} (not delivered in ${data.environment})`);
    } catch (err) { toast.error("Attachment test failed: " + err.message); }
    finally { setRunningAttach(false); }
  };

  // Global campaign-sending pause control. Resuming flips sending_paused=false AND
  // acknowledges the P1 release (so the fail-closed startup pause does not re-arm).
  const [sendPause, setSendPause] = useState(null);
  const [pauseBusy, setPauseBusy] = useState(false);
  const [confirmResume, setConfirmResume] = useState(false);

  const loadSendPause = useCallback(async () => {
    try {
      const res = await fetch(`${API}/campaigns/bespoke/config/sending-pause`, { credentials: "include" });
      if (res.ok) setSendPause(await res.json());
    } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { loadSendPause(); }, [loadSendPause]);

  const setSending = async (paused) => {
    setPauseBusy(true);
    try {
      const res = await fetch(`${API}/campaigns/bespoke/config/sending-pause`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ paused }),
      });
      const data = await res.json();
      if (!res.ok) { toast.error(data.detail || "Failed to update sending"); return; }
      setConfirmResume(false);
      await loadSendPause();
      toast.success(paused ? "Campaign sending PAUSED" : "Campaign sending RESUMED");
    } catch (err) { toast.error("Failed: " + err.message); }
    finally { setPauseBusy(false); }
  };

  const renderTab = () => {
    switch (activeTab) {
      case "rex-ops": return <RexOperationsTab />;
      case "system-status": return <SystemStatusPanel />;
      case "ai-models": return <AiModelsTab />;
      case "memory": return <MemoryTab />;
      case "email": return <EmailRecipientsTab />;
      case "senders": return <EmailSendersTab />;
      case "targets": return <RevenueTargetsTab />;
      case "pipeline": return <PipelineSettingsTab />;
      case "fx": return <FxRatesAdminTab />;
      case "data": return <DataManagementTab />;
      case "approvers": return <ContentApproversTab />;
      case "jobs": return <ScheduledJobsTab />;
      case "waits": return <WaitsSettingsTab />;
      case "authority": return <AuthorityControlsTab />;
      case "knowledge": return <KnowledgeOverviewTab />;
      case "risk": return <RiskOverridesTab />;
      case "integrations": return <IntegrationsTab user={user} />;
      case "cover-library": return <CoverLibraryAdmin />;
      case "health": return <SystemHealthTab />;
      default: return null;
    }
  };

  return (
    <div data-testid="admin-console" className="p-2 sm:p-6 md:p-8 lg:p-10 max-w-[1200px] mx-auto">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }} data-testid="admin-console-title">
          Admin Console
        </h1>
        <p className="text-xs sm:text-sm text-slate-500 mt-1">System administration, integrations, and governance controls</p>
      </div>

      {/* Global campaign-sending pause control */}
      <Card className={`mb-6 ${sendPause?.paused ? "border-red-200 bg-red-50/50" : "border-emerald-200 bg-emerald-50/50"}`} data-testid="sending-pause-card">
        <CardContent className="py-4 space-y-3">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <p className={`text-sm font-semibold flex items-center gap-1.5 ${sendPause?.paused ? "text-red-900" : "text-emerald-900"}`}>
                <Send className="h-4 w-4" /> Campaign Sending —{" "}
                <span data-testid="sending-pause-state">{sendPause == null ? "…" : sendPause.paused ? "PAUSED" : "ACTIVE"}</span>
              </p>
              <p className={`text-xs mt-0.5 ${sendPause?.paused ? "text-red-700" : "text-emerald-700"}`}>
                {sendPause?.paused
                  ? "All outbound email is held. Resuming enables real campaign and system emails to send."
                  : "Outbound email is active. Campaigns and system emails will send per their schedules."}
                {sendPause?.updated_by && (
                  <> {" "}· last changed by {sendPause.updated_by}{sendPause.updated_at ? ` on ${new Date(sendPause.updated_at).toLocaleString()}` : ""}.</>
                )}
              </p>
            </div>
            {sendPause != null && (sendPause.paused ? (
              <Button onClick={() => setConfirmResume(true)} disabled={pauseBusy}
                className="bg-emerald-600 hover:bg-emerald-700 gap-2 whitespace-nowrap" size="sm"
                data-testid="resume-sending-btn">
                {pauseBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Resume Sending
              </Button>
            ) : (
              <Button onClick={() => setSending(true)} disabled={pauseBusy}
                variant="outline" className="border-red-300 text-red-700 hover:bg-red-50 gap-2 whitespace-nowrap" size="sm"
                data-testid="pause-sending-btn">
                {pauseBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <X className="h-4 w-4" />} Pause Sending
              </Button>
            ))}
          </div>

          {confirmResume && (
            <div className="bg-white border border-emerald-300 rounded-lg p-3 text-xs space-y-2" data-testid="resume-confirm">
              <p className="text-slate-700">
                <strong>Resume all outbound sending?</strong> This enables real campaign emails and system
                emails (digests, notifications) to be delivered. This cannot be "unsent". Make sure reply
                monitoring is healthy and you intend to go live now.
              </p>
              <div className="flex gap-2">
                <Button onClick={() => setSending(false)} disabled={pauseBusy}
                  className="bg-emerald-600 hover:bg-emerald-700 gap-2" size="sm" data-testid="resume-confirm-btn">
                  {pauseBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Yes, resume sending
                </Button>
                <Button onClick={() => setConfirmResume(false)} variant="outline" size="sm" data-testid="resume-cancel-btn">
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Attachment delivery verification — one controlled test email with 3 files (always visible at top) */}
      <Card className="border-amber-200 bg-amber-50/50 mb-6" data-testid="attachment-test-card">
        <CardContent className="py-4 space-y-3">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-amber-900 flex items-center gap-1.5">
                <ShieldCheck className="h-4 w-4" /> Attachment Delivery Test
              </p>
              <p className="text-xs text-amber-700 mt-0.5">
                Sends ONE test email with three attachments to <strong>info@resiligent.com</strong>.
                It does not touch any campaign and does not change the sending pause.
              </p>
            </div>
            <Button
              onClick={() => runAttachmentTest(false)}
              disabled={runningAttach}
              className="bg-amber-600 hover:bg-amber-700 gap-2 whitespace-nowrap"
              size="sm"
              data-testid="run-attachment-test-btn"
            >
              {runningAttach ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              {runningAttach ? "Sending..." : "Send Attachment Test"}
            </Button>
          </div>

          {attachTest && (
            <div className="bg-white border border-amber-200 rounded-lg p-3 text-xs space-y-1.5" data-testid="attachment-test-result">
              <div className="flex items-center justify-between">
                <span className="font-semibold text-amber-900">Result</span>
                <button onClick={() => setAttachTest(null)} className="text-gray-400 hover:text-gray-600"><X className="h-3.5 w-3.5" /></button>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><span className="text-gray-500">Delivered externally:</span>{" "}
                  <span className={attachTest.delivered_externally ? "font-bold text-emerald-600" : "font-bold text-amber-600"}>
                    {attachTest.delivered_externally ? "YES" : "NO"}
                  </span>
                </div>
                <div><span className="text-gray-500">Provider outcome:</span> <span className="font-mono">{attachTest.guard_outcome}</span></div>
                <div><span className="text-gray-500">Environment:</span> {attachTest.environment} {attachTest.is_production ? "(production)" : ""}</div>
                <div><span className="text-gray-500">Recipient:</span> {attachTest.recipient}</div>
                <div><span className="text-gray-500">Pause before/after:</span> {String(attachTest.sending_paused_before)} / {String(attachTest.sending_paused_after)}</div>
                <div><span className="text-gray-500">Campaign queue:</span> {attachTest.campaign_queue_untouched ? "untouched" : "CHANGED"}</div>
              </div>
              <div><span className="text-gray-500">Attachments:</span> {(attachTest.attachments || []).join(", ")}</div>
              <div className="text-gray-600">{attachTest.note}</div>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar tabs */}
        <nav className="lg:w-56 flex-shrink-0">
          <div className="flex lg:flex-col gap-1 overflow-x-auto lg:overflow-visible pb-2 lg:pb-0">
            {TABS.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  data-testid={`tab-${tab.id}`}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
                    isActive
                      ? "bg-blue-50 text-blue-700"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`}
                >
                  <Icon className={`h-4 w-4 flex-shrink-0 ${isActive ? "text-blue-600" : "text-slate-400"}`} />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </nav>

        {/* Tab content */}
        <div className="flex-1 min-w-0">
          {renderTab()}
        </div>
      </div>
    </div>
  );
}
